import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import DealTextsTab from '../components/deal-detail/DealTextsTab';
import FundingConfirmationsTab from '../components/deal-detail/FundingConfirmationsTab';
import SenderSelect from '../components/email/SenderSelect';
import ComposeDealEmailDialog from '../components/email/ComposeDealEmailDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Edit, ChevronDown, ChevronLeft, ChevronRight, Settings, Trash2, AlertTriangle, Undo2, RefreshCw, ArrowRightLeft, DollarSign, XCircle, FileText, Mail, Lock, Plus } from 'lucide-react';
import { toast } from 'sonner';

// Import extracted components
import {
  MoveTransactionDialog,
  DocumentsTab,
  EditDealDialog,
  FullEditDealDialog,
  TransactionsTab,
  SyndicationTab,
  SummaryTab,
  DetailsTab,
  RefinanceWizard,
  DefaultRecoveryTab,
  DefaultStatusWorkflowDialog,
} from '../components/deal-detail';
import BounceTransactionDialog from '../components/deal-detail/BounceTransactionDialog';
import SettlementModal from '../components/deal-detail/SettlementModal';
import DefaultResetDialog from '../components/deal-detail/DefaultResetDialog';
import ContractDialog from '../components/deal-detail/ContractDialog';
// Part C (Feb 2026) — focused resolution modals + dispatch
// BL-026: CommissionPaidResolutionModal import removed — modal deleted; commission is derived.
import BrokerCommissionResolutionModal from '../components/deal-detail/resolution-modals/BrokerCommissionResolutionModal';
import EstimatedEndDateResolutionModal from '../components/deal-detail/resolution-modals/EstimatedEndDateResolutionModal';
import MerchantLinkResolutionModal from '../components/deal-detail/resolution-modals/MerchantLinkResolutionModal';
import BrokerLinkResolutionModal from '../components/deal-detail/resolution-modals/BrokerLinkResolutionModal';
import FunderLinkResolutionModal from '../components/deal-detail/resolution-modals/FunderLinkResolutionModal';
import { emitDealUpdate } from '../hooks/useDealUpdates';
import useDealUpdates from '../hooks/useDealUpdates';

// Import custom hooks
import { useDealDetail } from '../hooks/useDealDetail';
import { formatCurrency, formatDate } from '../lib/formatters';
import { expectedPayoffDate } from '../lib/termDates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Return today's date in YYYY-MM-DD using local timezone (avoids UTC off-by-one)
const getTodayLocal = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const getStatusColor = (status) => {
  const colors = {
    'Funded': 'bg-green-100 text-green-800',
    'Payment Issues': 'bg-yellow-100 text-yellow-800',
    'In-House Collection': 'bg-orange-100 text-orange-800',
    'Default': 'bg-red-200 text-red-900',
    'Collected': 'bg-blue-100 text-blue-800',
    'Paid in Full': 'bg-purple-100 text-purple-800',
    'Paid in Full (Refinanced)': 'bg-indigo-100 text-indigo-800',
    'Paid in Full / Settled': 'bg-teal-100 text-teal-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const DealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders, user } = useAuth();
  const initialTab = searchParams.get('tab') || 'summary';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Use custom hook for data fetching
  const {
    deal,
    setDeal,
    merchant,
    broker,
    funder,
    transactions,
    syndications,
    documents,
    investors,
    refinanceInfo,
    loading,
    refetch,
    refetchTransactions,
  } = useDealDetail(id, getAuthHeaders);

  // BL-020: Cross-tab + tab-focus staleness detection.
  // Subscriber: any tab that emits `emitDealUpdate(...)` (e.g. a resolution
  // modal in another tab, the Data Health worklist) triggers a refetch here.
  useDealUpdates(React.useCallback(() => {
    refetch();
  }, [refetch]));

  // Tab-focus refetch: when the user returns to this tab, ask the backend
  // for the deal's current version. If it's higher than what we last saw,
  // refetch the full deal. Catches backend-triggered changes (cron, CSV
  // import, multi-user edits) that BroadcastChannel can't see.
  React.useEffect(() => {
    if (!deal?.id) return undefined;
    const checkStaleness = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await axios.get(`${API}/deals/${id}/version`, { headers: getAuthHeaders() });
        const remote = Number(res.data?.version || 0);
        const local = Number(deal?.version || 0);
        if (remote > local) {
          refetch();
        }
      } catch (_e) {
        // Fail silently — staleness check is best-effort, never blocks the UI.
      }
    };
    const handler = () => { checkStaleness(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [deal?.id, deal?.version, id, getAuthHeaders, refetch]);

  // Use backend-provided computed values (Single Source of Truth)
  const paymentsMade = deal?.payments_made || 0;
  const effectiveBalance = deal?.balance_with_fees ?? deal?.balance ?? 0;

  // Lawyer Collected & Written Off — surfaced as separate context tiles when
  // non-zero. Amount Paid stays as direct merchant ACH only; these are
  // additional money-flow categories an operator needs to see at a glance
  // without drilling into Transactions.
  const lawyerCollected = (transactions || [])
    .filter((t) => t.type === 'LAWYER_COLLECTION' && (t.status || '').toLowerCase() !== 'void')
    .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
  const writtenOff = (transactions || [])
    .filter((t) => t.type === 'WRITE_OFF' && (t.status || '').toLowerCase() !== 'void')
    .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

  // Dialog states
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [syndicationDialogOpen, setSyndicationDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [editDealDialogOpen, setEditDealDialogOpen] = useState(false);
  // Part C (Feb 2026) — focused resolution modal state, keyed by missing_field.
  const [resolveModalKey, setResolveModalKey] = useState(null);
  // 08/20 contract generation (Josh): new vs refi contract dialogs; refi
  // deducts the current balance so the merchant nets the remainder.
  const [contractKind, setContractKind] = useState(null); // null | 'new' | 'refi'
  const [fullEditDialogOpen, setFullEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Cancel deal states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [settlementModalOpen, setSettlementModalOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  // Default dialog states
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);
  // Default-status workflow dialog (May 2026) — opens AFTER the backend
  // status flip to Default succeeds, via either the Mark-As-Default button
  // or the Edit Deal form's status dropdown. See
  // components/deal-detail/DefaultStatusWorkflowDialog.js.
  const [defaultWorkflowOpen, setDefaultWorkflowOpen] = useState(false);
  const [reverseDefaultDialogOpen, setReverseDefaultDialogOpen] = useState(false);
  // 11/02 (Josh): leaving Default = reset to the pre-default state; the popup lists GET /deals/{id}/default-reset-preview
  const [defaultResetPreview, setDefaultResetPreview] = useState(null);
  const [defaultResetLoading, setDefaultResetLoading] = useState(false);
  const [defaultResetTarget, setDefaultResetTarget] = useState(null);   // status picked in the dropdown; null = Reverse Default button
  const [defaultPreview, setDefaultPreview] = useState(null);
  const [defaultClawback, setDefaultClawback] = useState(false);   // "is this a clawback?" answer
  const [processingDefault, setProcessingDefault] = useState(false);
  const [defaultDate, setDefaultDate] = useState('');
  const [defaultFeeOverride, setDefaultFeeOverride] = useState('');
  // sendDefaultEmail / defaultLawFirmEmail state removed May 2026 — the
  // post-flip DefaultStatusWorkflowDialog now owns lawyer assignment +
  // email package sending.
  
  // Refinance wizard states
  const [refinanceWizardOpen, setRefinanceWizardOpen] = useState(false);
  const [refinanceWizardMode, setRefinanceWizardMode] = useState('from_old');
  
  // Edit states
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editingSyndication, setEditingSyndication] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [targetDealId, setTargetDealId] = useState('');
  const [allDeals, setAllDeals] = useState([]);
  const [changingStatus, setChangingStatus] = useState(false);
  const [savingDeal, setSavingDeal] = useState(false);
  const [editDealForm, setEditDealForm] = useState({});
  
  // Transfer Credit states
  const [transferCreditDialogOpen, setTransferCreditDialogOpen] = useState(false);
  const [transferCreditForm, setTransferCreditForm] = useState({
    targetDealId: '',
    amount: '',
    notes: ''
  });
  const [transferTargetSearch, setTransferTargetSearch] = useState('');
  const [transferTargetOpen, setTransferTargetOpen] = useState(false);
  const [transferringCredit, setTransferringCredit] = useState(false);

  // Deal notes state
  const [dealNotes, setDealNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Sync deal notes when deal loads
  useEffect(() => {
    if (deal?.notes !== undefined) {
      setDealNotes(deal.notes || '');
      setNotesDirty(false);
    }
  }, [deal?.notes]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.patch(`${API}/deals/${deal.id}`, { notes: dealNotes }, { headers: getAuthHeaders() });
      setDeal(prev => ({ ...prev, notes: dealNotes }));
      setNotesDirty(false);
      toast.success('Notes saved');
    } catch (err) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // EPO states
  const [epoDialogOpen, setEpoDialogOpen] = useState(false);
  const [epoPreview, setEpoPreview] = useState(null);
  const [processingEpo, setProcessingEpo] = useState(false);
  const [epoForm, setEpoForm] = useState({
    newPayback: '',
    discountAmount: '',
    newCommissionRate: '',
    epoDate: getTodayLocal(),
    notes: '',
    feeWaiverAmount: ''
  });

  // Navigation states for prev/next deal
  const [adjacentDeals, setAdjacentDeals] = useState({ prev: null, next: null });

  // Form states
  const [transactionForm, setTransactionForm] = useState({
    date: getTodayLocal(),
    amount: '',
    type: 'PAYMENT',
    description: 'ACH',
    reference_number: '',
    notes: '',
    addBounceFee: true,
    bounceFeeAmount: '35',
    lawyerGrossCollected: '',
    lawyerFee: ''
  });
  
  const [syndicationForm, setSyndicationForm] = useState({
    syndicator_id: '',
    ownership_percentage: '',
    role: 'INVESTOR',
    collect_fees: true
  });
  
  const [documentForm, setDocumentForm] = useState({
    document_name: '',
    document_type: 'Contract',
    file_url: '',
    notes: ''
  });

  // Permission checks
  const isAdmin = user?.roles?.includes('admin');
  const isSuperAdmin = user?.roles?.includes('super_admin'); // 10/08 roles pass 3: deletes are super-only
  // 10/07/2026 (Josh): underwriters see deals and generate documents but edit nothing yet.
  const canChangeStatus = isAdmin;
  const canEditDeal = isAdmin;

  // Fetch adjacent deals for prev/next navigation
  useEffect(() => {
    const fetchAdjacentDeals = async () => {
      if (!deal?.deal_id) return;
      
      try {
        // Fetch all deals sorted by deal_id to find prev/next
        const response = await axios.get(`${API}/deals?limit=1000`, { headers: getAuthHeaders() });
        const allDeals = response.data
          .filter(d => d.deal_id != null && !isNaN(parseInt(d.deal_id)))
          .map(d => ({ deal_id: parseInt(d.deal_id), id: d.id }))
          .sort((a, b) => a.deal_id - b.deal_id);
        
        const currentDealId = parseInt(deal.deal_id);
        const currentIndex = allDeals.findIndex(d => d.deal_id === currentDealId);
        
        if (currentIndex === -1) {
          setAdjacentDeals({ prev: null, next: null });
          return;
        }
        
        setAdjacentDeals({
          prev: currentIndex > 0 ? allDeals[currentIndex - 1] : null,
          next: currentIndex < allDeals.length - 1 ? allDeals[currentIndex + 1] : null
        });
      } catch (error) {
        console.error('Failed to fetch adjacent deals:', error);
        setAdjacentDeals({ prev: null, next: null });
      }
    };
    
    fetchAdjacentDeals();
  }, [deal?.deal_id]); // Only re-fetch when deal changes

  // Publish the open deal number so the CRM assistant (MILA) can retrieve
  // DEAL-scoped memory for it without guessing from the message text. This is
  // the structured CRM context that takes priority over regex inference.
  useEffect(() => {
    window.__PFS_ACTIVE_DEAL_NUMBER__ = deal?.deal_id != null ? String(deal.deal_id) : null;
    return () => { window.__PFS_ACTIVE_DEAL_NUMBER__ = null; };
  }, [deal?.deal_id]);

  // Navigation handlers
  const navigateToPrevDeal = () => {
    if (adjacentDeals.prev) {
      navigate(`/deals/${adjacentDeals.prev.deal_id}`);
    }
  };

  const navigateToNextDeal = () => {
    if (adjacentDeals.next) {
      navigate(`/deals/${adjacentDeals.next.deal_id}`);
    }
  };

  // Delete deal handler
  const handleDeleteDeal = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/deals/${id}`, { headers: getAuthHeaders() });
      toast.success(`Deal #${deal.deal_id} deleted successfully`);
      navigate('/deals');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete deal');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDeal = async () => {
    setCancelling(true);
    try {
      await axios.patch(`${API}/deals/${id}/cancel`, { note: cancelNote }, { headers: getAuthHeaders() });
      toast.success('Deal cancelled');
      setCancelDialogOpen(false);
      setCancelNote('');
      setDeal(prev => ({ ...prev, status: 'Cancelled' }));
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel deal');
    } finally {
      setCancelling(false);
    }
  };


  // Handlers
  const handleStatusChange = async (newStatus) => {
    if (!canChangeStatus || !newStatus || newStatus === deal.status) return;
    
    // If selecting "Default", open the default dialog instead of directly changing status
    if (newStatus === 'Default') {
      handleOpenDefaultDialog();
      return;
    }

    // If selecting "Cancelled", use the dedicated cancel endpoint
    if (newStatus === 'Cancelled') {
      setCancelDialogOpen(true);
      return;
    }

    // If selecting "Paid in Full / Settled", open settlement modal (write-off, method, legal fees)
    if (newStatus === 'Paid in Full / Settled') {
      setSettlementModalOpen(true);
      return;
    }

    // 11/02 (Josh): "by switching out of default status, because there was collections activity on the deal,
    // everything will be removed" — confirm first, listing what the reset voids / deletes / clears
    if (deal.status === 'Default' || deal.status === 'Defaulted') {
      openDefaultReset(newStatus);
      return;
    }
    
    setChangingStatus(true);
    try {
      await axios.patch(`${API}/deals/${id}/status`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`Status changed to ${newStatus}`);
      setDeal(prev => ({ ...prev, status: newStatus }));
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setChangingStatus(false);
    }
  };

  // 11/02 (Josh): one popup for every exit from Default (dropdown or Reverse Default button) listing what the reset removes
  const openDefaultReset = async (targetStatus) => {
    setDefaultResetTarget(targetStatus);
    setDefaultResetPreview(null);
    setDefaultResetLoading(true);
    setReverseDefaultDialogOpen(true);
    try {
      const response = await axios.get(`${API}/deals/${id}/default-reset-preview`, { headers: getAuthHeaders() });
      setDefaultResetPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load what leaving Default would remove');
      setReverseDefaultDialogOpen(false);
    } finally {
      setDefaultResetLoading(false);
    }
  };

  const confirmDefaultReset = async () => {
    if (!defaultResetTarget) return handleReverseDefault();
    setProcessingDefault(true);
    setChangingStatus(true);
    try {
      const response = await axios.patch(`${API}/deals/${id}/status`, { status: defaultResetTarget }, { headers: getAuthHeaders() });
      toast.success(response.data?.message || `Status changed to ${defaultResetTarget}`);
      setReverseDefaultDialogOpen(false);
      setDeal(prev => ({ ...prev, status: defaultResetTarget }));
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setProcessingDefault(false);
      setChangingStatus(false);
    }
  };

  // Default handling
  const handleOpenDefaultDialog = async () => {
    try {
      const response = await axios.get(`${API}/deals/${id}/default-preview`, { headers: getAuthHeaders() });
      setDefaultPreview(response.data);
      setDefaultClawback(false);
      setDefaultDate(getTodayLocal());
      setDefaultFeeOverride(response.data.calculation?.fee_amount?.toString() || '0');
      setDefaultDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load default preview');
    }
  };

  const handleMarkAsDefault = async (forceRefinanced = false) => {
    setProcessingDefault(true);
    try {
      const body = {};
      if (defaultDate) body.default_date = defaultDate;
      const feeVal = parseFloat(defaultFeeOverride);
      if (!isNaN(feeVal)) body.override_fee = feeVal;
      // Email is now owned by DefaultStatusWorkflowDialog (May 2026 spec).
      // The old send_email / law_firm_email body params were removed from
      // the mark-default endpoint — they would be silently ignored by the
      // backend.
      
      // Add force parameter for refinanced deals
      const forceParam = forceRefinanced ? '?force=true' : '';
      const response = await axios.post(`${API}/deals/${id}/mark-default${forceParam}`, body, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      // 09/29 (Josh): default inside the 20-business-day window → "is this a clawback?"
      if (defaultClawback && defaultPreview?.clawback?.should_ask) {
        try {
          const cb = await axios.post(`${API}/commissions/clawback`, {
            deal_uuid: deal?.id || defaultPreview.deal_id,
            reason: `Defaulted ${defaultPreview.clawback.business_days_since_funding} business day(s) after funding`,
          }, { headers: getAuthHeaders() });
          toast.success(`Commission clawback requested: ${formatCurrency(cb.data?.clawback_amount)}`);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Deal defaulted, but the clawback could not be recorded — use the Default tab');
        }
      }
      setDefaultDialogOpen(false);
      setDefaultPreview(null);
      setDefaultClawback(false);
      refetch();
      // Open the post-flip workflow: "Was this sent to a lawyer?"
      setDefaultWorkflowOpen(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'deal_is_refinanced') {
        toast.error(`Cannot default: ${detail.message}. Use "Mark as Refinanced" instead.`);
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to mark deal as defaulted');
      }
    } finally {
      setProcessingDefault(false);
    }
  };

  const handleMarkAsRefinanced = async () => {
    setProcessingDefault(true);
    try {
      const refiTo = defaultPreview?.refi_to_deal_id_number || '';
      const response = await axios.post(
        `${API}/deals/${id}/mark-refinanced?refi_to_deal_id_number=${refiTo}`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setDefaultDialogOpen(false);
      setDefaultPreview(null);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark deal as refinanced');
    } finally {
      setProcessingDefault(false);
    }
  };

  const handleReverseDefault = async () => {
    setProcessingDefault(true);
    try {
      const response = await axios.post(`${API}/deals/${id}/reverse-default`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      setReverseDefaultDialogOpen(false);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse default');
    } finally {
      setProcessingDefault(false);
    }
  };

  // EPO handlers
  const handleOpenEpoDialog = async () => {
    try {
      const response = await axios.get(`${API}/deals/${id}/epo-preview`, { headers: getAuthHeaders() });
      const preview = response.data;
      setEpoPreview(preview);
      setEpoForm({
        newPayback: preview.effective_payback?.toString() || '',
        discountAmount: '0',
        newCommissionRate: preview.commission_rate?.toString() || '0',
        epoDate: getTodayLocal(),
        notes: '',
        feeWaiverAmount: '0'
      });
      setEpoDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load EPO preview');
    }
  };

  const handleEpoPaybackChange = (value) => {
    const newPayback = parseFloat(value) || 0;
    const effectivePayback = epoPreview?.effective_payback || 0;
    const discount = Math.max(0, effectivePayback - newPayback);
    setEpoForm(prev => ({
      ...prev,
      newPayback: value,
      discountAmount: discount.toFixed(2)
    }));
  };

  const handleEpoDiscountChange = (value) => {
    const discount = parseFloat(value) || 0;
    const effectivePayback = epoPreview?.effective_payback || 0;
    const newPayback = Math.max(0, effectivePayback - discount);
    setEpoForm(prev => ({
      ...prev,
      discountAmount: value,
      newPayback: newPayback.toFixed(2)
    }));
  };

  const handleApplyEpo = async () => {
    const newPayback = parseFloat(epoForm.newPayback);
    if (!newPayback || newPayback <= 0) {
      toast.error('Please enter a valid new payback amount');
      return;
    }
    const newCommRate = parseFloat(epoForm.newCommissionRate);
    if (isNaN(newCommRate) || newCommRate < 0) {
      toast.error('Please enter a valid commission rate');
      return;
    }
    const feeWaiver = parseFloat(epoForm.feeWaiverAmount) || 0;
    if (feeWaiver < 0) {
      toast.error('Fee waiver amount cannot be negative');
      return;
    }

    setProcessingEpo(true);
    try {
      const response = await axios.post(`${API}/deals/${id}/apply-epo`, {
        new_payback: newPayback,
        new_commission_rate: newCommRate,
        epo_date: epoForm.epoDate,
        notes: epoForm.notes,
        fee_waiver_amount: feeWaiver
      }, { headers: getAuthHeaders() });

      toast.success(response.data.message);
      setEpoDialogOpen(false);
      setEpoPreview(null);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply EPO');
    } finally {
      setProcessingEpo(false);
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    
    // Handle Lawyer Collection via dedicated endpoint
    if (transactionForm.type === 'Lawyer Collection' || transactionForm.type === 'LAWYER_COLLECTION') {
      const gross = parseFloat(transactionForm.lawyerGrossCollected);
      if (!gross || gross <= 0) {
        toast.error('Enter a valid gross collected amount');
        return;
      }
      try {
        const payload = {
          date: transactionForm.date,
          gross_collected: gross,
          remit_reference: transactionForm.reference_number || '',
          notes: transactionForm.notes || ''
        };
        // Include lawyer fee if user entered one (including 0)
        if (transactionForm.lawyerFee !== '' && transactionForm.lawyerFee !== undefined) {
          payload.lawyer_fee = parseFloat(transactionForm.lawyerFee) || 0;
        }
        await axios.post(`${API}/default-recovery/${id}/collections`, payload, { headers: getAuthHeaders() });
        toast.success('Lawyer collection recorded');
        setTransactionDialogOpen(false);
        setEditingTransaction(null);
        setTransactionForm({
          date: getTodayLocal(),
          amount: '',
          type: 'PAYMENT',
          description: 'ACH',
          reference_number: '',
          notes: '',
          addBounceFee: true,
          bounceFeeAmount: '35',
          customFeeName: '',
          lawyerGrossCollected: '',
          lawyerFee: ''
        });
        refetchTransactions();
        refetch(); // 08/12 (Josh): tiles/balances refresh themselves after manual entries
        return;
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Failed to add lawyer collection');
        return;
      }
    }
    
    let finalType = transactionForm.type;
    if (transactionForm.type === 'Payment' || transactionForm.type === 'PAYMENT') {
      finalType = 'PAYMENT';
    } else if (transactionForm.type === 'Fee') {
      finalType = `Fee - ${transactionForm.description}`;
    } else if (transactionForm.type === 'Bounced Payment' || transactionForm.type === 'ACH_RETURN') {
      finalType = 'ACH_RETURN';
    } else if (transactionForm.type === 'Misc Fee') {
      finalType = `Fee: ${transactionForm.customFeeName || 'Miscellaneous'}`;
    } else if (transactionForm.type === 'CUSTOM_FEE') {
      if (!transactionForm.customFeeName?.trim()) {
        toast.error('Enter a fee name for the custom fee');
        return;
      }
      // CUSTOM_FEE is a canonical ledger type — operator-entered miscellaneous
      // fees (CC fees, processing fees, etc.). Distinct from DEFAULT_FEE (the
      // 25% mark-default penalty). DO NOT remap to DEFAULT_FEE.
      finalType = 'CUSTOM_FEE';
    }
    
    // Auto-generate reference for Discount/Fee Discount if not provided
    let referenceNumber = transactionForm.reference_number;
    if ((transactionForm.type === 'Discount' || transactionForm.type === 'DISCOUNT' || transactionForm.type === 'Fee Discount' || transactionForm.type === 'FEE_DISCOUNT') && !referenceNumber) {
      referenceNumber = `DISC-${Date.now().toString(36).toUpperCase()}`;
    }
    // Auto-generate reference for Custom Fee if not provided
    if (transactionForm.type === 'CUSTOM_FEE' && !referenceNumber) {
      referenceNumber = `CUSTOMFEE-${Date.now().toString(36).toUpperCase()}`;
    }
    // Auto-generate reference for Misc Fee if not provided
    if (transactionForm.type === 'Misc Fee' && !referenceNumber) {
      referenceNumber = `MISCFEE-${Date.now().toString(36).toUpperCase()}`;
    }
    // Auto-generate reference for Deferred Fee if not provided
    if (transactionForm.type === 'DEFERRED_FEE' && !referenceNumber) {
      referenceNumber = `DEF-${Date.now().toString(36).slice(-8).toLowerCase()}`;
    }
    
    try {
      // For bounced payments with a reference, check if we should mark existing payment as bounced
      if ((transactionForm.type === 'Bounced Payment' || transactionForm.type === 'ACH_RETURN') && referenceNumber) {
        // Try to find and mark existing payment as bounced
        try {
          const markRes = await axios.post(`${API}/transactions/mark-bounced-by-reference`, {
            deal_id: deal.id,
            reference: referenceNumber,
            bounce_date: new Date(transactionForm.date).toISOString(),
            return_code: transactionForm.description || 'R01',
            add_fee: transactionForm.addBounceFee,
            fee_amount: parseFloat(transactionForm.bounceFeeAmount || 35)
          }, { headers: getAuthHeaders() });
          
          toast.success(markRes.data.message || 'Payment marked as bounced');
          setTransactionDialogOpen(false);
          setEditingTransaction(null);
          setTransactionForm({
            date: getTodayLocal(),
            amount: '',
            type: 'PAYMENT',
            description: 'ACH',
            reference_number: '',
            notes: '',
            addBounceFee: true,
            bounceFeeAmount: '35',
            customFeeName: '',
            lawyerGrossCollected: '',
            lawyerFee: ''
          });
          refetchTransactions();
          refetch();
          return;
        } catch (markError) {
          // If no existing payment found, create new bounced payment with unique reference
          console.log('No existing payment found, creating new bounced payment');
          referenceNumber = `BOUNCED:${referenceNumber}:${Date.now().toString(36).toUpperCase()}`;
        }
      } else if ((transactionForm.type === 'Bounced Payment' || transactionForm.type === 'ACH_RETURN') && !referenceNumber) {
        referenceNumber = `BOUNCED-${Date.now().toString(36).toUpperCase()}`;
      }
      
      // Manual payment-method convention (Josh 07/21): Zelle/Venmo/CC/etc get
      // reference "<Method> - MM/DD/YYYY" (Wire = just "Wire"; ACH keeps real refs),
      // description persists, and notes echo the method.
      let finalNotes = transactionForm.type === 'CUSTOM_FEE'
        ? (transactionForm.customFeeName + (transactionForm.notes ? ' — ' + transactionForm.notes : ''))
        : (transactionForm.notes || '');
      const isPaymentType = transactionForm.type === 'Payment' || transactionForm.type === 'PAYMENT';
      const desc = transactionForm.description || '';
      const isMethodDesc = isPaymentType && desc && desc.toUpperCase() !== 'ACH';
      if (isMethodDesc) {
        const methodLabel = desc.replace(/\s*\(.*\)\s*$/, '').trim(); // "CC (Credit Card)" -> "CC"
        if (!referenceNumber) {
          if (/^wire/i.test(methodLabel)) {
            referenceNumber = 'Wire';
          } else {
            const [y, m, d] = String(transactionForm.date).split('-');
            referenceNumber = `${methodLabel} - ${m}/${d}/${y}`;
          }
        }
        const methodNote = `${methodLabel} payment`;
        if (!finalNotes.toLowerCase().includes(methodLabel.toLowerCase())) {
          finalNotes = finalNotes ? `${methodNote} — ${finalNotes}` : methodNote;
        }
      }
      const transactionData = {
        deal_id: deal.id,
        type: finalType,
        amount: parseFloat(transactionForm.amount),
        date: new Date(transactionForm.date).toISOString(),
        reference_number: referenceNumber || '',
        description: desc || '',
        notes: finalNotes
      };
      
      // For bounced payments, set status to bounced
      if (transactionForm.type === 'Bounced Payment' || transactionForm.type === 'ACH_RETURN') {
        transactionData.status = 'bounced';
      }
      
      if (editingTransaction) {
        const updateRes = await axios.put(`${API}/transactions/${editingTransaction.id}`, transactionData, { headers: getAuthHeaders() });
        toast.success('Transaction updated successfully');
        // 10/27 (Josh): "Add Bounce Fee" ticked while EDITING an existing return used to be dropped silently
        if ((transactionForm.type === 'Bounced Payment' || transactionForm.type === 'ACH_RETURN') && transactionForm.addBounceFee) {
          try {
            const feeRes = await axios.post(`${API}/transactions/${editingTransaction.id}/bounce-fee`,
              { amount: parseFloat(transactionForm.bounceFeeAmount || 35) }, { headers: getAuthHeaders() });
            toast.success(feeRes.data?.message || 'Bounce fee added');
          } catch (feeErr) {
            toast.error(feeErr.response?.data?.detail || 'Bounce fee was NOT added');
          }
        }
        if (updateRes.data?.syndication_recompute_blocked) {
          const codes = (updateRes.data.accounting_issues || []).join(', ');
          toast.warning(`Saved, but syndication figures were NOT refreshed — this deal needs accounting review${codes ? ` (${codes.replace(/_/g, ' ')})` : ''}.`);
        }
      } else {
        const createRes = await axios.post(`${API}/transactions`, transactionData, { headers: getAuthHeaders() });

        // If bounced payment or ACH_RETURN with fee, create the fee transaction too
        if ((transactionForm.type === 'Bounced Payment' || transactionForm.type === 'ACH_RETURN') && transactionForm.addBounceFee) {
          const feeData = {
            deal_id: deal.id,
            type: 'BOUNCE_FEE',
            amount: parseFloat(transactionForm.bounceFeeAmount || 35),
            date: new Date(transactionForm.date).toISOString(),
            status: 'successful',
            reference_number: referenceNumber ? `BOUNCE_FEE:${referenceNumber}` : `BOUNCE_FEE:${Date.now().toString(36).toUpperCase()}`,
            notes: `Bounce Fee (${transactionForm.description || 'R01'})`
          };
          await axios.post(`${API}/transactions`, feeData, { headers: getAuthHeaders() });
        }
        
        const isBounce = transactionForm.type === 'Bounced Payment' || transactionForm.type === 'ACH_RETURN';
        toast.success(isBounce
          ? `ACH Return added${transactionForm.addBounceFee ? ' with $' + (transactionForm.bounceFeeAmount || 35) + ' fee' : ''}`
          : 'Transaction added successfully');
        if (createRes.data?.syndication_recompute_blocked) {
          const codes = (createRes.data.accounting_issues || []).join(', ');
          toast.warning(`Saved, but syndication figures were NOT refreshed — this deal needs accounting review${codes ? ` (${codes.replace(/_/g, ' ')})` : ''}.`);
        }
      }
      
      setTransactionDialogOpen(false);
      setEditingTransaction(null);
      setTransactionForm({
        date: getTodayLocal(),
        amount: '',
        type: 'PAYMENT',
        description: 'ACH',
        reference_number: '',
        notes: '',
        addBounceFee: true,
        bounceFeeAmount: '35',
        customFeeName: '',
        lawyerGrossCollected: '',
        lawyerFee: ''
      });
      refetchTransactions();
      refetch(); // 08/12 (Josh): tiles/balances refresh themselves after manual entries
      // Paid-off popup (Josh 07/24): announce when this entry zeroes the balance
      if (deal && !String(deal.status || '').startsWith('Paid in Full')) {
        try {
          const fresh = await axios.get(`${API}/deals/${deal.id}`, { headers: getAuthHeaders() });
          const fd = fresh.data;
          const bal = fd.balance_with_fees ?? fd.balance;
          if (fd.status === 'Paid in Full' || (bal !== undefined && bal !== null && bal <= 0.01)) {
            window.alert(`Deal #${fd.deal_id} is fully paid off!\n\nBalance is $0.00 — status is now "${fd.status}".`);
            refetch();
          }
        } catch (e) { /* non-critical */ }
      }
    } catch (error) {
      const rawDetail = error.response?.data?.detail;
      // Pydantic 422 returns an array of {type, loc, msg, ...} objects — stringify them.
      const detail = Array.isArray(rawDetail)
        ? rawDetail.map(e => (typeof e === 'string' ? e : (e.msg || JSON.stringify(e)))).join('; ')
        : (typeof rawDetail === 'string' ? rawDetail : '');
      if (detail.includes('duplicate key') || detail.includes('already exists')) {
        toast.error('A transaction with this reference already exists. Try a different reference number.');
      } else {
        toast.error(detail || (editingTransaction ? 'Failed to update transaction' : 'Failed to add transaction'));
      }
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    
    try {
      await axios.delete(`${API}/transactions/${transactionId}`, { headers: getAuthHeaders() });
      toast.success('Transaction deleted successfully');
      refetchTransactions();
      refetch();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTransactions.length === 0) {
      toast.error('Please select transactions to delete');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedTransactions.length} transaction(s)?`)) return;
    
    try {
      await axios.post(`${API}/transactions/bulk-delete`, selectedTransactions, { headers: getAuthHeaders() });
      toast.success(`Deleted ${selectedTransactions.length} transaction(s) successfully`);
      setSelectedTransactions([]);
      refetchTransactions();
      refetch();
    } catch (error) {
      toast.error('Failed to delete transactions');
    }
  };

  const handleBulkEdit = async (transactionIds, updates) => {
    if (transactionIds.length === 0) {
      toast.error('Please select transactions to edit');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/transactions/bulk-update`, {
        transaction_ids: transactionIds,
        updates: updates
      }, { headers: getAuthHeaders() });
      
      toast.success(`Updated ${res.data.updated} transaction(s) successfully`);
      setSelectedTransactions([]);
      refetchTransactions();
      refetch();
    } catch (error) {
      toast.error('Failed to update transactions: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleMarkBounced = async (transactionId) => {
    // Single canonical bounce path: every UI flow (inline-confirm pill, dialog,
    // bulk import) routes through POST /transactions/{id}/bounce. The legacy
    // /mark-bounced endpoint hardcoded a $35 fee and ignored the dialog's
    // "Charge bounce fee" checkbox — that divergence caused users to see fees
    // applied even after un-checking the box. Inline-confirm is the implicit
    // "yes, charge $35" path; the dialog is the explicit-control path.
    try {
      const res = await axios.post(
        `${API}/transactions/${transactionId}/bounce`,
        {
          return_code: 'R01',
          charge_fee: true,
          fee_amount: 35,
          bounce_date: new Date().toISOString().slice(0, 10),
        },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      refetchTransactions();
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as bounced');
    }
  };

  // One-click refund handler (no modal, no confirmation)
  const handleRefundTransaction = async (transactionId) => {
    if (deal && deal.status === 'Paid in Full') {
      if (!window.confirm(`This deal is currently "Paid in Full". Refunding this payment will reopen a balance and change the deal status back to "Funded"${deal.terminal_locked ? ' (the terminal lock will be cleared)' : ''}.\n\nContinue?`)) return;
    }
    // 08/12 (Josh): ask for a reference number before submitting the refund.
    // Blank = auto-generate (REF-xxxxxxxx); Cancel = abort the refund.
    const refInput = window.prompt(
      'Reference number for this refund?\n\nLeave blank to auto-generate one.'
    );
    if (refInput === null) return; // operator cancelled
    try {
      const res = await axios.post(
        `${API}/transactions/${transactionId}/refund`,
        { reference: refInput.trim() || null },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      refetchTransactions();
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create refund');
    }
  };

  // Reverse a Deferred Fee — voids both legs (target DEFERRED_FEE +
  // source FEE_DEFERRED_OUT) atomically. Optional reason captured via prompt.
  const handleReverseDefer = async (transaction) => {
    if (!window.confirm(`Reverse this $${Math.abs(transaction.amount).toFixed(2)} deferred fee?\n\nBoth legs (this row and the offset on the source deal) will be voided.`)) return;
    const reason = window.prompt('Reason for reversal (optional):', '') || '';
    try {
      await axios.post(
        `${API}/transactions/defer-fee/reverse`,
        { target_transaction_id: transaction.id, reason },
        { headers: getAuthHeaders() }
      );
      toast.success('Deferred fee reversed — both legs voided');
      refetchTransactions();
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse deferred fee');
    }
  };

  // Bounce dialog state + handlers
  const [bounceDialogOpen, setBounceDialogOpen] = useState(false);
  const [bounceTarget, setBounceTarget] = useState(null);

  // 10/22 (Josh): move an ACH Return onto the pull it reverses, from the row itself.
  const handleRelinkReturn = async (returnId, paymentId) => {
    try {
      const res = await axios.post(`${API}/transactions/${returnId}/relink`, { payment_id: paymentId }, { headers: getAuthHeaders() });
      toast.success(res.data.message || 'Return re-linked');
      refetchTransactions();
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not re-link the return');
    }
  };

  const handleOpenBounceDialog = (transaction) => {
    setBounceTarget(transaction);
    setBounceDialogOpen(true);
  };

  const handleSubmitBounce = async (payload) => {
    try {
      const { transaction_id, ...rest } = payload;
      const res = await axios.post(
        `${API}/transactions/${transaction_id}/bounce`,
        rest,
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || 'Payment bounced');
      refetchTransactions();
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bounce payment');
      throw error;
    }
  };

  // Transfer Credit handlers
  const handleOpenTransferCreditDialog = async () => {
    setTransferCreditDialogOpen(true);
    // Pre-fill with overage amount if available
    if (effectiveBalance < 0) {
      setTransferCreditForm(prev => ({
        ...prev,
        amount: Math.abs(effectiveBalance).toFixed(2)
      }));
    }
    // Load deals list if not already loaded
    if (allDeals.length === 0) {
      try {
        const dealsRes = await axios.get(`${API}/deals?limit=1000`, { headers: getAuthHeaders() });
        setAllDeals(dealsRes.data.sort((a, b) => a.deal_id - b.deal_id));
      } catch (err) {
        toast.error('Failed to load deals list');
      }
    }
  };

  const handleTransferCredit = async () => {
    if (!transferCreditForm.targetDealId) {
      toast.error('Please select a target deal');
      return;
    }
    
    const amount = parseFloat(transferCreditForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }
    
    setTransferringCredit(true);
    try {
      const response = await axios.post(`${API}/transactions/transfer-credit`, {
        source_deal_id: deal.id,
        target_deal_id: transferCreditForm.targetDealId,
        amount: amount,
        notes: transferCreditForm.notes
      }, { headers: getAuthHeaders() });
      
      toast.success(response.data.message);
      setTransferCreditDialogOpen(false);
      setTransferCreditForm({ targetDealId: '', amount: '', notes: '' });
      refetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer credit');
    } finally {
      setTransferringCredit(false);
    }
  };

  const handleOpenMoveDialog = async () => {
    setMoveDialogOpen(true);
    if (allDeals.length === 0) {
      try {
        const dealsRes = await axios.get(`${API}/deals?limit=1000`, { headers: getAuthHeaders() });
        setAllDeals(dealsRes.data.sort((a, b) => a.deal_id - b.deal_id));
      } catch (err) {
        toast.error('Failed to load deals list');
      }
    }
  };

  const handleMoveTransactions = async () => {
    if (selectedTransactions.length === 0) {
      toast.error('Please select transactions to move');
      return;
    }
    
    if (!targetDealId) {
      toast.error('Please enter a target deal ID');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/transactions/bulk-move`, selectedTransactions, {
        params: { target_deal_id: targetDealId },
        headers: getAuthHeaders()
      });
      
      toast.success(response.data.message);
      setMoveDialogOpen(false);
      setTargetDealId('');
      setSelectedTransactions([]);
      refetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move transactions');
    }
  };

  const handleAddSyndication = async (e) => {
    e.preventDefault();
    try {
      if (editingSyndication) {
        await axios.put(`${API}/syndications/${editingSyndication.id}`, {
          deal_id: id,
          syndicator_id: syndicationForm.syndicator_id,
          ownership_percentage: parseFloat(syndicationForm.ownership_percentage),
          role: syndicationForm.role || 'INVESTOR',
          collect_fees: syndicationForm.collect_fees
        }, { headers: getAuthHeaders() });
        toast.success('Syndicator updated successfully');
      } else {
        const response = await axios.post(`${API}/syndications`, {
          deal_id: id,
          syndicator_id: syndicationForm.syndicator_id,
          ownership_percentage: parseFloat(syndicationForm.ownership_percentage),
          collect_fees: syndicationForm.collect_fees
        }, { headers: getAuthHeaders() });
        
        if (response.data.retroactive_applied && response.data.retroactive_amount > 0) {
          toast.success(
            `Syndicator added with retroactive credit of ${formatCurrency(response.data.retroactive_amount)} from past payments!`,
            { duration: 5000 }
          );
        } else {
          toast.success('Syndicator added successfully');
        }
      }
      
      setSyndicationDialogOpen(false);
      setEditingSyndication(null);
      setSyndicationForm({
        syndicator_id: '',
        ownership_percentage: '',
        role: 'INVESTOR',
        collect_fees: true
      });
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add syndicator');
    }
  };

  const handleDeleteSyndication = async (syndicationId) => {
    // Find the syndication to get syndicator name
    const syndication = syndications.find(s => s.id === syndicationId);
    const syndicatorName = syndication?.syndicator_name || 'this syndicator';
    
    // Require typing DELETE
    const confirmation = prompt(
      `To delete syndication for "${syndicatorName}", type DELETE:\n\n` +
      `This action will:\n` +
      `- Remove this syndicator from the deal\n` +
      `- Create an audit log entry\n` +
      `- Mark deal allocations for recompute`
    );
    
    if (confirmation !== 'DELETE') {
      toast.error('Deletion cancelled - must type DELETE to confirm');
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/syndications/${syndicationId}?confirmation=DELETE&reason=Manual deletion via Deal UI`, 
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message || 'Syndication deleted successfully');
      setSyndicationDialogOpen(false);
      setEditingSyndication(null);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete syndication');
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/deals/${id}/documents`, {
        deal_id: id,
        uploaded_by: 'current_user_id',
        ...documentForm
      }, { headers: getAuthHeaders() });
      
      toast.success('Document added successfully');
      setDocumentDialogOpen(false);
      setDocumentForm({
        document_name: '',
        document_type: 'Contract',
        file_url: '',
        notes: ''
      });
      refetch();
    } catch (error) {
      toast.error('Failed to add document');
    }
  };

  // 10/26 (Josh: "404 when opening the pdf"): uploaded files live behind GET /api/documents/files/{name} on the API
  // host and require the bearer token, so a plain <a href> (relative to the web host) can never open them. Open a tab
  // synchronously (popup blockers), fetch the file with the session, then point the tab at the blob.
  const handleOpenDocument = async (doc) => {
    const url = doc.file_url || '';
    if (!url.startsWith('/api/')) { window.open(url, '_blank', 'noopener'); return; }
    const tab = window.open('', '_blank');
    try {
      const r = await axios.get(`${BACKEND_URL}${url}`, { headers: getAuthHeaders(), responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: r.headers['content-type'] || 'application/octet-stream' }));
      if (tab) tab.location = blobUrl; else window.open(blobUrl, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
    } catch (error) {
      if (tab) tab.close();
      toast.error(error.response?.status === 404 ? 'That file is missing on the server.' : 'Could not open the document.');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers: getAuthHeaders() });
      toast.success('Document deleted successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Document generation handler
  const handleGenerateDocument = async (docType) => {
    try {
      toast.info('Generating document...');
      const response = await axios.get(
        `${API}/deals/${id}/documents/generate/${docType}`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `${docType}_${deal?.deal_id || 'document'}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate document');
    }
  };

  // Email document state & handlers
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDocType, setEmailDocType] = useState('');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSender, setEmailSender] = useState(null);     // 10/13: From dropdown (admin mailboxes only for documents)
  const [emailSenderId, setEmailSenderId] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);    // 10/13: free-form compose from the deal page

  const openEmailDialog = async (docType) => {
    setEmailDocType(docType);
    setEmailRecipient('');
    setEmailSubject('');
    setEmailMessage('');
    try {
      const res = await axios.get(`${API}/deals/${id}/documents/email-context`, { headers: getAuthHeaders() });
      setEmailRecipients(res.data.recipients || []);
      setEmailSender(res.data.sender || null);
      setEmailSenderId(res.data.sender?.default_id || '');
      if (res.data.recipients?.length > 0) {
        setEmailRecipient(res.data.recipients[0].email);
      }
    } catch { setEmailRecipients([]); setEmailSender(null); setEmailSenderId(''); }
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailRecipient) { toast.error('Enter a recipient email'); return; }
    setSendingEmail(true);
    try {
      const res = await axios.post(
        `${API}/deals/${id}/documents/email/${emailDocType}`,
        {
          recipient_email: emailRecipient,
          subject: emailSubject || undefined,
          message: emailMessage || undefined,
          sender_id: emailSenderId || undefined,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Sent to ${res.data.sent_to}`);
      setEmailDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally { setSendingEmail(false); }
  };


  // Edit Deal handlers
  const handleOpenEditDeal = () => {
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    };

    setEditDealForm({
      deal_id: deal.deal_id?.toString() || '',
      deal_shortcode: deal.deal_shortcode || '',
      deal_name: deal.deal_name || '',
      funding_date: formatDateForInput(deal.funding_date),
      funding_amount: deal.funding_amount?.toString() || '',
      rate: deal.rate?.toString() || '',
      term_days: deal.term_days?.toString() || '',
      payback: deal.payback?.toString() || '',
      payment_amount: deal.payment_amount?.toString() || '',
      estimated_end_date: formatDateForInput(deal.estimated_end_date),
      payment_frequency: deal.payment_frequency || (deal.weekly ? 'Weekly' : 'Daily'),
      fees: deal.fees?.toString() || '0',
      refi_balance_payoff: deal.refi_balance_payoff?.toString() || '0',
      funder_percentage: deal.funder_percentage?.toString() || '0',
      commission_rate: deal.commission_rate?.toString() || '',
      broker_split_rate: deal.broker_split_rate?.toString() || '',
      // BL-026: commission_paid removed — derived from commission_rate × funding_amount.
      broker_commission: deal.broker_commission == null ? '' : deal.broker_commission.toString(),
    });
    setEditDealDialogOpen(true);
  };

  // Part C (Feb 2026) — focused-modal dispatch.
  // Routes amber-warning clicks to the right focused modal based on the
  // warning's missing_field string. Unhandled missing_fields fall back to
  // the simple Edit Deal dialog so behavior is never a dead-end.
  const RESOLVE_DISPATCH = {
    // BL-026: deal.commission_paid removed; warnings point at deal.commission_rate.
    // 08/10 (Josh: "these hyperlinks are not working") — the 'commission_rate'
    // resolution modal was removed in BL-026 but this mapping still routed to
    // it, making the click a silent no-op. No entry = falls through to the
    // standard Edit Deal dialog, which has the Commission Rate field.
    'deal.broker_commission':     'broker_commission',
    'deal.estimated_end_date':    'estimated_end_date',
    'deal.company_id':            'merchant_link',
    'deal.broker_id':             'broker_link',
    'deal.funder_id':             'funder_link',
  };

  // 08/10 (Josh): the Legal/Default tab must stay visible for LIFE once a
  // lawyer was involved — a Paid in Full / Settled deal that went through
  // legal keeps its recovery history accessible.
  const showLegalTab =
    deal?.status === 'Default' ||
    deal?.settlement_method === 'Attorney/Legal' ||
    (deal?.legal_fees || 0) > 0 ||
    (transactions || []).some(
      (t) =>
        (t.type === 'LAWYER_COLLECTION' || t.type === 'Lawyer Collection') &&
        (t.status || '').toLowerCase() !== 'void'
    );

  const handleResolveWarning = (missingField) => {
    const modalKey = RESOLVE_DISPATCH[missingField];
    if (modalKey) {
      setResolveModalKey(modalKey);
    } else {
      // Unhandled missing_field (e.g. compound "deal.funding_amount/fees/refi/deposit"
      // for net_advance) → fall through to the simple Edit Deal dialog.
      handleOpenEditDeal();
    }
  };


  const handleEditDealFieldChange = (field, value) => {
    setEditDealForm(prev => {
      const updated = { ...prev, [field]: value };
      
      if (field === 'funding_amount' || field === 'rate') {
        const fundingAmt = parseFloat(field === 'funding_amount' ? value : prev.funding_amount) || 0;
        const rate = parseFloat(field === 'rate' ? value : prev.rate) || 0;
        if (fundingAmt > 0 && rate > 0) {
          updated.payback = (fundingAmt * rate).toFixed(2);
        }
      }

      // Reverse direction: editing Payback recomputes Rate from current Funding.
      // Use 6dp for rate so a payback of e.g. $11,375 round-trips exactly through
      // a 6-decimal factor (no snapping to multiples of funding × 0.0001).
      if (field === 'payback') {
        const fundingAmt = parseFloat(prev.funding_amount) || 0;
        const payback = parseFloat(value) || 0;
        if (fundingAmt > 0 && payback > 0) {
          updated.rate = (payback / fundingAmt).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
        }
      }
      // Editing Funding when only Payback is set should derive Rate too.
      if (field === 'funding_amount') {
        const fundingAmt = parseFloat(value) || 0;
        const rate = parseFloat(prev.rate) || 0;
        const payback = parseFloat(prev.payback) || 0;
        if (fundingAmt > 0 && (!rate || rate <= 0) && payback > 0) {
          updated.rate = (payback / fundingAmt).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
        }
      }
      
      if (field === 'payback' || field === 'term_days') {
        const payback = parseFloat(field === 'payback' ? value : prev.payback) || 0;
        const term = parseFloat(field === 'term_days' ? value : prev.term_days) || 0;
        if (payback > 0 && term > 0) {
          updated.payment_amount = (payback / term).toFixed(2);
        }
      }
      // Expected Payoff Date follows term × payment frequency (Josh 10/01): 13 weekly payments =
      // 13 weeks, not 13 days. Typing a date by hand still wins (that field has its own handler).
      if (field === 'term_days' || field === 'payment_frequency' || field === 'funding_date') {
        const term = parseFloat(field === 'term_days' ? value : prev.term_days) || 0;
        const fundingDate = field === 'funding_date' ? value : prev.funding_date;
        const frequency = field === 'payment_frequency' ? value : prev.payment_frequency;
        const est = term > 0 ? expectedPayoffDate(fundingDate, term, frequency) : null;
        if (est) updated.estimated_end_date = est;
      }

      // 08/10 (Josh): Commission Rate (%) ↔ Scheduled Commission ($) work in
      // tandem — filling either derives the other (scheduled = funding × rate%).
      if (field === 'commission_rate') {
        const fundingAmt = parseFloat(prev.funding_amount) || 0;
        const ratePct = parseFloat(value);
        if (fundingAmt > 0 && !Number.isNaN(ratePct)) {
          updated.broker_commission = (fundingAmt * ratePct / 100).toFixed(2);
        }
      }
      if (field === 'broker_commission') {
        const fundingAmt = parseFloat(prev.funding_amount) || 0;
        const amt = parseFloat(value);
        if (fundingAmt > 0 && !Number.isNaN(amt)) {
          updated.commission_rate = ((amt / fundingAmt) * 100).toFixed(4)
            .replace(/0+$/, '').replace(/\.$/, '');
        }
      }
      // Funding change re-derives Scheduled Commission from the rate.
      if (field === 'funding_amount') {
        const fundingAmt = parseFloat(value) || 0;
        const ratePct = parseFloat(prev.commission_rate);
        if (fundingAmt > 0 && !Number.isNaN(ratePct)) {
          updated.broker_commission = (fundingAmt * ratePct / 100).toFixed(2);
        }
      }
      
      if (field === 'funding_date' || field === 'term_days') {
        const fundingDate = field === 'funding_date' ? value : prev.funding_date;
        const term = parseFloat(field === 'term_days' ? value : prev.term_days) || 0;
        if (fundingDate && term > 0) {
          const date = new Date(fundingDate);
          let daysAdded = 0;
          while (daysAdded < term) {
            date.setDate(date.getDate() + 1);
            const dayOfWeek = date.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
              daysAdded++;
            }
          }
          updated.estimated_end_date = date.toISOString().split('T')[0];
        }
      }
      
      return updated;
    });
  };

  const handleSaveDeal = async () => {
    try {
      setSavingDeal(true);
      
      const updateData = {
        deal_id: editDealForm.deal_id,
        deal_shortcode: editDealForm.deal_shortcode,
        deal_name: editDealForm.deal_name,
        funding_date: editDealForm.funding_date ? new Date(editDealForm.funding_date).toISOString() : null,
        funding_amount: parseFloat(editDealForm.funding_amount) || 0,
        rate: parseFloat(editDealForm.rate) || 0,
        term_days: parseFloat(editDealForm.term_days) || 0,
        payback: parseFloat(editDealForm.payback) || 0,
        payment_amount: parseFloat(editDealForm.payment_amount) || 0,
        estimated_end_date: editDealForm.estimated_end_date ? new Date(editDealForm.estimated_end_date).toISOString() : null,
        payment_frequency: editDealForm.payment_frequency || 'Daily',
        fees: parseFloat(editDealForm.fees) || 0,
        refi_balance_payoff: parseFloat(editDealForm.refi_balance_payoff) || 0,
        funder_percentage: parseFloat(editDealForm.funder_percentage) || 0,
        commission_rate: parseFloat(editDealForm.commission_rate) || null,
        broker_split_rate: parseFloat(editDealForm.broker_split_rate) || null,
        // BL-026: commission_paid removed — derived from commission_rate × funding_amount.
        broker_commission: editDealForm.broker_commission === '' || editDealForm.broker_commission == null
          ? null
          : parseFloat(editDealForm.broker_commission),
        broker_id: deal.broker_id,
        company_id: deal.company_id,
        funder_id: deal.funder_id,
        status: deal.status,
        balance: deal.balance,
        payments_made: deal.payments_made,
        percent_paid_thru: deal.percent_paid_thru,
        payments_til_pif: deal.payments_til_pif,
        with_fees: deal.with_fees,
        refi: deal.refi,
        deposit_amount: deal.deposit_amount,
      };
      
      // Use PATCH for partial updates
      await axios.patch(`${API}/deals/${id}`, updateData, { headers: getAuthHeaders() });
      toast.success('Deal updated successfully');
      emitDealUpdate({ dealId: id, source: 'edit-deal-dialog' });
      setEditDealDialogOpen(false);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update deal');
    } finally {
      setSavingDeal(false);
    }
  };

  const downloadTransactionsCSV = () => {
    const csvHeaders = ['Type', 'Date', 'Amount', 'Status', 'Reference', 'Notes', 'Running Balance'];
    
    // Sort by date ascending for the CSV
    const sortedTransactions = [...transactions].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    const csvRows = sortedTransactions.map(trans => [
      trans.type || '',
      formatDate(trans.date),
      trans.amount || 0,
      trans.status || 'successful',
      trans.reference || trans.reference_number || '',
      (trans.notes || trans.description || '').replace(/"/g, '""'),
      trans.running_balance != null ? trans.running_balance : ''
    ]);
    
    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deal-${deal.deal_id}-transactions.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!deal) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Deal not found</p>
        </div>
      </Layout>
    );
  }

  const profit = (paymentsMade || 0) - (deal.funding_amount || 0);
  const roi = deal.funding_amount > 0 ? (profit / deal.funding_amount * 100) : 0;

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header Section */}
        <div className="mb-6">
          {/* Navigation Row */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/deals')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Deals
            </Button>
            
            {/* Prev/Next Deal Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={navigateToPrevDeal}
                disabled={!adjacentDeals.prev}
                className="flex items-center gap-1"
                data-testid="prev-deal-btn"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Prev</span>
                {adjacentDeals.prev && <span className="text-gray-500">#{adjacentDeals.prev.deal_id}</span>}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={navigateToNextDeal}
                disabled={!adjacentDeals.next}
                className="flex items-center gap-1"
                data-testid="next-deal-btn"
              >
                {adjacentDeals.next && <span className="text-gray-500">#{adjacentDeals.next.deal_id}</span>}
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900">Deal #{deal.deal_id}</h1>
                {canChangeStatus && deal.status !== 'Cancelled' ? (
                  <Select 
                    value={deal.status} 
                    onValueChange={handleStatusChange}
                    disabled={changingStatus}
                  >
                    <SelectTrigger data-testid="deal-status-select-trigger" className={`w-[160px] h-8 ${getStatusColor(deal.status)}`}>
                      <SelectValue>{deal.status}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Funded">Funded</SelectItem>
                      <SelectItem value="Payment Issues">Payment Issues</SelectItem>
                      <SelectItem value="In-House Collection">In-House Collection</SelectItem>
                      <SelectItem value="Default">Default</SelectItem>
                      <SelectItem value="Collected">Collected</SelectItem>
                      <SelectItem value="Paid in Full">Paid in Full</SelectItem>
                      <SelectItem value="Paid in Full (Refinanced)">Paid in Full (Refinanced)</SelectItem>
                      <SelectItem value="Paid in Full / Settled">Paid in Full / Settled</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={getStatusColor(deal.status)}>{deal.status}</Badge>
                )}
                {deal.cofunded_partner_id && (
                  <span className="text-xs text-indigo-700 bg-indigo-100 px-2 py-1 rounded" title="Co-funded deal: the partner pays the broker; PFS / DIB positions owe the partner their share of the commission" data-testid="deal-cofunded-badge">
                    Co-funded · {deal.cofunded_partner_name || 'partner'}
                  </span>
                )}
                {/* BL-037 / Principle #11 — Cancelled status suppresses
                    the "100% Paid" badge. Cancelled is historical-record-only;
                    the deal carries no active financial position. */}
                {deal.percent_paid_thru >= 100 && deal.status !== 'Paid in Full' && deal.status !== 'Cancelled' && (
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded" data-testid="deal-100-paid-badge">100% Paid</span>
                )}
                {/* BL-019: terminal_locked indicator. Visible when an operator
                    has declared a terminal status; the recalc engine will not
                    auto-mutate this deal's status until POST /reopen clears
                    the lock. */}
                {deal.terminal_locked && (
                  <span
                    className="text-xs text-amber-700 bg-amber-100 border border-amber-300 px-2 py-1 rounded inline-flex items-center gap-1"
                    title="Terminal status locked — recalc cannot auto-change status. Operator must explicitly reopen to alter."
                    data-testid="deal-terminal-locked-badge"
                  >
                    <Lock className="h-3 w-3" />
                    Terminal Locked
                  </span>
                )}
              </div>
              {/* 08/10 (Josh): deal name links to the merchant's contact profile */}
              {deal.company_id ? (
                <Link
                  to={`/contacts/${deal.company_id}`}
                  className="text-xl text-gray-600 mt-1 inline-block hover:text-blue-600 hover:underline"
                  data-testid="deal-name-contact-link"
                  title="Open merchant contact profile"
                >
                  {deal.deal_name}
                </Link>
              ) : (
                <p className="text-xl text-gray-600 mt-1">{deal.deal_name}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">Shortcode: {deal.deal_shortcode}</p>
            </div>
            <div className="flex items-center gap-2">
              {canEditDeal && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Deal
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleOpenEditDeal}>
                      <Edit className="h-4 w-4 mr-2" />
                      Quick Edit (Basic Info)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setFullEditDialogOpen(true)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Full Edit (All Fields)
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        {deal?.status !== 'Cancelled' && (
                          <DropdownMenuItem
                            onClick={() => setCancelDialogOpen(true)}
                            className="text-gray-600 focus:text-gray-600 focus:bg-gray-50"
                            data-testid="cancel-deal-btn"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Cancel Deal
                          </DropdownMenuItem>
                        )}
                        {isSuperAdmin && (
                        <DropdownMenuItem 
                          onClick={() => setDeleteDialogOpen(true)}
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Deal
                        </DropdownMenuItem>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Generate Document Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="generate-document-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Document
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem onClick={() => setComposeOpen(true)} data-testid="compose-email">
                    <Mail className="h-4 w-4 mr-2 text-slate-600" />
                    Compose Email…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleGenerateDocument('zbl')} data-testid="generate-zbl">
                    <FileText className="h-4 w-4 mr-2 text-green-600" />
                    Download ZBL
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openEmailDialog('zbl')} data-testid="email-zbl">
                    <Mail className="h-4 w-4 mr-2 text-green-600" />
                    Email ZBL
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleGenerateDocument('payoff')} data-testid="generate-payoff">
                    <FileText className="h-4 w-4 mr-2 text-blue-600" />
                    Download Payoff Letter
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openEmailDialog('payoff')} data-testid="email-payoff">
                    <Mail className="h-4 w-4 mr-2 text-blue-600" />
                    Email Payoff Letter
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleGenerateDocument('payment-history')} data-testid="generate-payment-history">
                    <FileText className="h-4 w-4 mr-2 text-purple-600" />
                    Download Payment History
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openEmailDialog('payment-history')} data-testid="email-payment-history">
                    <Mail className="h-4 w-4 mr-2 text-purple-600" />
                    Email Payment History
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setContractKind('new')} data-testid="generate-new-contract">
                        <FileText className="h-4 w-4 mr-2 text-indigo-600" />
                        Generate New Contract
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setContractKind('refi')} data-testid="generate-refi-contract">
                        <FileText className="h-4 w-4 mr-2 text-amber-600" />
                        Generate Refi Contract
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Refinance Buttons - Admin only */}
              {isAdmin && deal.status !== 'Paid in Full (Refinanced)' && deal.status !== 'Paid in Full' && deal.status !== 'Paid in Full / Settled' && deal.balance > 0 && (
                <Button 
                  variant="outline"
                  className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                  onClick={() => {
                    setRefinanceWizardMode('from_old');
                    setRefinanceWizardOpen(true);
                  }}
                  data-testid="refinance-this-deal-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refinance Into New Deal
                </Button>
              )}
              {isAdmin && deal.status !== 'Paid in Full (Refinanced)' && deal.status !== 'Paid in Full' && deal.status !== 'Refinanced' && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setRefinanceWizardMode('from_new');
                    setRefinanceWizardOpen(true);
                  }}
                  data-testid="add-old-deals-btn"
                >
                  Add Old Deals (Refi From)
                </Button>
              )}
              {/* Default/Reverse Default Buttons - Admin only */}
              {isAdmin && deal.status !== 'Default' && deal.status !== 'Paid in Full' && deal.status !== 'Paid in Full (Refinanced)' && deal.status !== 'Paid in Full / Settled' && (
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={handleOpenDefaultDialog}
                  data-testid="mark-as-default-btn"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Mark as Default
                </Button>
              )}
              {/* EPO Button - Admin only, show when deal has balance */}
              {isAdmin && deal.status !== 'Paid in Full' && deal.status !== 'Paid in Full (Refinanced)' && deal.status !== 'Paid in Full / Settled' && deal.status !== 'Default' && deal.balance > 0 && (
                <Button 
                  variant="outline"
                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  onClick={handleOpenEpoDialog}
                  data-testid="epo-btn"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Early Pay Off (EPO)
                </Button>
              )}
              {isAdmin && deal.status === 'Default' && (
                <Button 
                  variant="outline" 
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  onClick={() => openDefaultReset(null)}
                >
                  <Undo2 className="h-4 w-4 mr-2" />
                  Reverse Default
                </Button>
              )}
              {/* Transfer Credit Button - Show when deal has overage (negative balance) */}
              {isAdmin && effectiveBalance < 0 && (
                <Button 
                  variant="outline" 
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  onClick={handleOpenTransferCreditDialog}
                  data-testid="transfer-credit-btn"
                >
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Transfer Credit ({formatCurrency(Math.abs(effectiveBalance))})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Refinance Info Banner */}
        {refinanceInfo && (refinanceInfo.refinanced_from || refinanceInfo.refinanced_to) && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-6">
                <div className="text-blue-600 font-medium">Refinance Info</div>
                {refinanceInfo.refinanced_from && (
                  <div className="text-sm">
                    <span className="text-gray-600">Refinanced from:</span>
                    {(refinanceInfo.refinanced_from_list || [refinanceInfo.refinanced_from]).map((rf) => (
                      <div key={rf.id} className="mt-1">
                        <button
                          onClick={() => navigate(`/deals/${rf.id}`)}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          Deal #{rf.deal_number} ({rf.shortcode})
                        </button>
                        <span className="text-gray-500 ml-2">
                          - paid off: {formatCurrency(rf.amount_paid_off)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {refinanceInfo.refinanced_to && (
                  <div className="text-sm">
                    <span className="text-gray-600">Refinanced into: </span>
                    <button
                      onClick={() => navigate(`/deals/${refinanceInfo.refinanced_to.id}`)}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      Deal #{refinanceInfo.refinanced_to.deal_number} ({refinanceInfo.refinanced_to.shortcode})
                    </button>
                    <span className="text-gray-500 ml-2">
                      - New funding: {formatCurrency(refinanceInfo.refinanced_to.new_funding_amount)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards - 8 Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 mb-6 pfs-kpi-grid">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Funded Amount</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(deal.funding_amount)}</div>
              <p className="text-xs text-gray-500" data-testid="kpi-factor">
                Factor: {(() => {
                  const fund = parseFloat(deal.funding_amount) || 0;
                  const pay = parseFloat(deal.payback) || 0;
                  const computed = fund > 0 ? pay / fund : 0;
                  const stored = parseFloat(deal.factor_rate || deal.factor || deal.rate) || 0;
                  const factor = stored > 0 ? stored : computed;
                  return factor > 0 ? factor.toFixed(3) : '—';
                })()}
              </p>
            </CardContent>
          </Card>

          {/* Net Amount card hidden per request — keep code for future re-enable */}
          {false && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Net Amount</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-cyan-600">{formatCurrency(deal.deposit_amount || 0)}</div>
              <p className="text-xs text-gray-500">To Merchant</p>
            </CardContent>
          </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Payback Amount</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${(deal.total_discounts || 0) > 0 ? 'text-gray-400 line-through' : 'text-purple-600'}`}>
                {formatCurrency(deal.payback)}
              </div>
              {(deal.total_discounts || 0) > 0 ? (
                <p className="text-xs text-green-600 font-medium">Effective: {formatCurrency(deal.effective_payback)}</p>
              ) : (
                <p className="text-xs text-gray-500">Daily: {formatCurrency(deal.payment_amount)}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Amount Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(paymentsMade)}</div>
              <p className="text-xs text-gray-500">{deal.payback > 0 ? ((paymentsMade / deal.payback) * 100).toFixed(1) : 0}% of payback</p>
            </CardContent>
          </Card>

          {/* Lawyer Collected — REAL cash received via lawyer; shown only when > 0.
              Separate from Amount Paid (direct merchant ACH) by design — operator
              needs to distinguish in-house collection from third-party collection. */}
          {lawyerCollected > 0 && (
            <Card className="border-indigo-200 bg-indigo-50" data-testid="lawyer-collected-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-indigo-700" data-testid="lawyer-collected-label">
                  Lawyer Collected
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-indigo-600" data-testid="lawyer-collected-value">
                  {formatCurrency(lawyerCollected)}
                </div>
                <p className="text-xs text-gray-500">via legal recovery</p>
              </CardContent>
            </Card>
          )}

          {/* Written Off — FORGIVEN debt (NOT cash collected); shown only when > 0.
              Visually distinct from green/cash tiles so the operator doesn't confuse
              "forgiven receivable" with "money received". */}
          {writtenOff > 0 && (
            <Card className="border-amber-200 bg-amber-50" data-testid="written-off-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-amber-700" data-testid="written-off-label">
                  Written Off
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-700" data-testid="written-off-value">
                  {formatCurrency(writtenOff)}
                </div>
                <p className="text-xs text-gray-500">forgiven debt</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Fees</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{formatCurrency(deal.total_fees || 0)}</div>
              <p className="text-xs text-gray-500">{transactions.filter(t => ['BOUNCE_FEE', 'DEFAULT_FEE', 'DEFERRED_FEE'].includes(t.type)).length} fee(s)</p>
            </CardContent>
          </Card>

          <Card className={(deal.total_discounts || 0) > 0 ? 'border-green-200 bg-green-50' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium ${(deal.total_discounts || 0) > 0 ? 'text-green-700' : 'text-gray-600'}`}>Discounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${(deal.total_discounts || 0) > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {formatCurrency(deal.total_discounts || 0)}
              </div>
              <p className="text-xs text-gray-500">{transactions.filter(t => t.type === 'DISCOUNT').length} discount(s)</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Balance Due</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const useRaw = isAdmin && deal.raw_balance !== undefined && deal.raw_balance !== null;
                // 08/05 (Josh): over-collected deals show ZERO here — the excess
                // lives in the Credits/Overage tiles. Positive raw shortfalls on
                // closed deals still show (the point of the admin true-balance view).
                const bal = useRaw ? Math.max(0, deal.raw_balance) : Math.max(0, deal.balance);
                return (
                  <div className={`text-2xl font-bold ${bal < 0 ? 'text-blue-600' : bal <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                    {formatCurrency(bal)}
                  </div>
                );
              })()}
              <p className="text-xs text-gray-500">RTR remaining</p>
            </CardContent>
          </Card>

          <Card className="border-slate-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600" data-testid="effective-balance-label">Effective Balance</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const useRaw = isAdmin && deal.raw_balance_with_fees !== undefined && deal.raw_balance_with_fees !== null;
                // 08/05 (Josh): clamp over-collection to zero — excess shows in Credits.
                const bal = useRaw ? Math.max(0, deal.raw_balance_with_fees) : Math.max(0, effectiveBalance);
                return (
                  <div className={`text-2xl font-bold ${bal < 0 ? 'text-blue-600' : bal <= 0 ? 'text-green-600' : 'text-slate-800'}`} data-testid="effective-balance-value">
                    {formatCurrency(bal)}
                  </div>
                );
              })()}
              <p className="text-xs text-gray-500">incl. fees</p>
            </CardContent>
          </Card>

          {/* Credits — net merchant credit (post-fees overage). Only colored when > 0.
              Positioned immediately to the left of Overage so the two related
              "money beyond payback" tiles read together. */}
          {(() => {
            // 08/05 (Josh): overages on closed deals land HERE instead of showing
            // as negative balances — include the admin raw over-collection too.
            const rawWfOver = (isAdmin && deal.raw_balance_with_fees != null)
              ? Math.max(0, -deal.raw_balance_with_fees) : 0;
            const credits = Math.max(0, -effectiveBalance, rawWfOver);
            const hasCredits = credits > 0;
            return (
              <Card className={hasCredits ? 'border-blue-200 bg-blue-50' : ''} data-testid="credits-card">
                <CardHeader className="pb-2">
                  <CardTitle className={`text-sm font-medium ${hasCredits ? 'text-blue-700' : 'text-gray-600'}`} data-testid="credits-label">
                    Credits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${hasCredits ? 'text-blue-600' : 'text-gray-400'}`} data-testid="credits-value">
                    {formatCurrency(credits)}
                  </div>
                  <p className="text-xs text-gray-500">{hasCredits ? 'Net of fees' : 'No credits'}</p>
                </CardContent>
              </Card>
            );
          })()}

          {/* Overage — TOTAL excess paid by merchant past payback (= Total Fees + Credits).
              Equivalent to max(0, -balance) since Effective Balance = balance + total_fees. */}
          {(() => {
            const overage = Math.max(0, -(deal.balance || 0));
            const hasOverage = overage > 0;
            return (
              <Card className={hasOverage ? 'border-blue-300 bg-blue-50' : 'border-dashed'}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-sm font-medium ${hasOverage ? 'text-blue-700' : 'text-gray-600'}`} data-testid="overage-label">
                    Overage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${hasOverage ? 'text-blue-600' : 'text-gray-400'}`} data-testid="overage-value">
                    {formatCurrency(overage)}
                  </div>
                  <p className="text-xs text-gray-500">
                    {hasOverage ? 'Incl. fees' : 'No overage'}
                  </p>
                </CardContent>
              </Card>
            );
          })()}
        </div>

        {/* Settlement Info — visible for settled deals */}
        {deal.status === 'Paid in Full / Settled' && (
          <Card
            className="mb-6 border-teal-300 bg-gradient-to-r from-teal-50 to-cyan-50"
            data-testid="settlement-info-card"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-teal-900 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-teal-500" />
                Settlement Info
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-teal-700 font-semibold">Settlement Amount</div>
                  <div className="text-xl font-bold text-teal-900 tabular-nums" data-testid="settlement-info-amount">
                    {/* 08/12 (Josh): standard for settlements — always show the
                        accepted figure; fall back to total collected when no
                        explicit agreed amount was stamped. */}
                    {formatCurrency(deal.settlement_amount || deal.payments_made || 0)}
                  </div>
                  {!deal.settlement_amount && (deal.payments_made || 0) > 0 && (
                    <div className="text-[10px] text-teal-600">total accepted</div>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Written Off</div>
                  <div className="text-xl font-bold text-amber-700 tabular-nums" data-testid="settlement-info-written-off">
                    {formatCurrency(deal.settlement_written_off || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-teal-700 font-semibold">Settlement Factor</div>
                  <div className="text-xl font-bold text-teal-900 tabular-nums" data-testid="settlement-info-factor">
                    {(deal.funding_amount || 0) > 0 && (deal.payments_made || 0) > 0
                      ? ((deal.payments_made / deal.funding_amount).toFixed(4).replace(/0+$/, '').replace(/\.$/, ''))
                      : '—'}
                  </div>
                  {(deal.rate || 0) > 0 && (
                    <div className="text-[10px] text-teal-600">vs {deal.rate} contract</div>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Settlement Date</div>
                  <div className="text-base font-semibold text-slate-900" data-testid="settlement-info-date">
                    {deal.settlement_date || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Method</div>
                  <div className="text-base font-semibold text-slate-900" data-testid="settlement-info-method">
                    {deal.settlement_method || '—'}
                    {deal.went_to_default && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
                        Defaulted
                      </span>
                    )}
                  </div>
                </div>
                {deal.settlement_method === 'Attorney/Legal' && (deal.legal_fees || 0) > 0 && (
                  <div className="sm:col-span-2 lg:col-span-4 pt-2 border-t border-teal-200">
                    <div className="text-xs uppercase tracking-wide text-purple-700 font-semibold">Legal Fees Paid</div>
                    <div className="text-lg font-bold text-purple-800 tabular-nums" data-testid="settlement-info-legal-fees">
                      {formatCurrency(deal.legal_fees)}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="transactions">Transactions ({transactions.length})</TabsTrigger>
            <TabsTrigger value="syndication">Syndication ({syndications.length})</TabsTrigger>
            <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
            {isAdmin && <TabsTrigger value="confirmation">Confirmation</TabsTrigger>}
            {isAdmin && <TabsTrigger value="texts">Texts</TabsTrigger>}  {/* 10/12 Josh: conversation on the deal */}
            {isAdmin && showLegalTab && <TabsTrigger value="default-recovery" data-testid="default-recovery-tab-trigger">Default / Recovery</TabsTrigger>}
          </TabsList>

          {isAdmin && <TabsContent value="confirmation"><FundingConfirmationsTab deal={deal} documents={documents} getAuthHeaders={getAuthHeaders} onDocumentsChanged={refetch} /></TabsContent>}

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            <SummaryTab deal={deal} merchant={merchant} broker={broker} funder={funder} refinanceInfo={refinanceInfo} getAuthHeaders={getAuthHeaders} onRefetch={refetch} isAdmin={isAdmin} onEditDeal={handleResolveWarning} />
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details">
            <DetailsTab deal={deal} getAuthHeaders={getAuthHeaders} onRefresh={refetch} />
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <TransactionsTab
              transactions={transactions}
              deal={deal}
              selectedTransactions={selectedTransactions}
              setSelectedTransactions={setSelectedTransactions}
              transactionDialogOpen={transactionDialogOpen}
              setTransactionDialogOpen={setTransactionDialogOpen}
              transactionForm={transactionForm}
              setTransactionForm={setTransactionForm}
              editingTransaction={editingTransaction}
              setEditingTransaction={setEditingTransaction}
              onAddTransaction={handleAddTransaction}
              onDeleteTransaction={isSuperAdmin ? handleDeleteTransaction : undefined}
              onBulkDelete={isSuperAdmin ? handleBulkDelete : undefined}
              onOpenMoveDialog={handleOpenMoveDialog}
              onBulkEdit={handleBulkEdit}
              onMarkBounced={handleMarkBounced}
              onRefundTransaction={handleRefundTransaction}
              onBounceTransaction={handleOpenBounceDialog}
              onRelinkReturn={handleRelinkReturn}
              onDownloadCSV={downloadTransactionsCSV}
              isAdmin={isAdmin}
              getAuthHeaders={getAuthHeaders}
              onUploadComplete={() => { refetchTransactions(); refetch(); }}
              onDeferred={() => { refetchTransactions(); refetch(); }}
              onReverseDefer={handleReverseDefer}
            />
            
            {/* Move Transactions Dialog */}
            <MoveTransactionDialog
              open={moveDialogOpen}
              onOpenChange={setMoveDialogOpen}
              selectedTransactions={selectedTransactions}
              allDeals={allDeals}
              currentDealId={id}
              targetDealId={targetDealId}
              setTargetDealId={setTargetDealId}
              onMove={handleMoveTransactions}
              onCancel={() => setMoveDialogOpen(false)}
            />

            {/* Bounce Payment Dialog */}
            <BounceTransactionDialog
              open={bounceDialogOpen}
              onOpenChange={setBounceDialogOpen}
              transaction={bounceTarget}
              onSubmit={handleSubmitBounce}
            />
          </TabsContent>

          {/* Syndication Tab */}
          <TabsContent value="syndication">
            {isAdmin && (
              <div className="mb-3 flex justify-end">
                <Button variant="outline" size="sm" onClick={() => navigate(`/deals/${id}/offering`)} data-testid="open-syndication-offering">
                  <DollarSign className="w-4 h-4 mr-1" /> Syndication offering (beta)
                </Button>
              </div>
            )}
            <SyndicationTab
              syndications={syndications}
              investors={investors}
              syndicationDialogOpen={syndicationDialogOpen}
              setSyndicationDialogOpen={setSyndicationDialogOpen}
              syndicationForm={syndicationForm}
              setSyndicationForm={setSyndicationForm}
              editingSyndication={editingSyndication}
              setEditingSyndication={setEditingSyndication}
              onAddSyndication={handleAddSyndication}
              onDeleteSyndication={isSuperAdmin ? handleDeleteSyndication : undefined}
              onRefetch={refetch}
              isAdmin={isAdmin}
              dealId={deal?.id}
              getAuthHeaders={getAuthHeaders}
            />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <DocumentsTab
              documents={documents}
              documentDialogOpen={documentDialogOpen}
              setDocumentDialogOpen={setDocumentDialogOpen}
              documentForm={documentForm}
              setDocumentForm={setDocumentForm}
              onAddDocument={handleAddDocument}
              onDeleteDocument={handleDeleteDocument}
              onOpenDocument={handleOpenDocument}
            />
          </TabsContent>

          {/* Texts Tab — 10/12 (Josh): SMS conversation + composer on the deal itself */}
          {isAdmin && (
            <TabsContent value="texts">
              <DealTextsTab deal={deal} getAuthHeaders={getAuthHeaders} isAdmin={isAdmin} />
            </TabsContent>
          )}

          {/* Default / Recovery Tab — 08/10 (Josh): "EVEN IF ITS PAID IN FULL
              OR PAID IN FULL SETTLED, BECAUSE IT HAD A LAWYER INVOLVED, THE
              LAWYER/LEGAL TAB SHOULD STILL SHOW" — visible on any deal with
              legal history, not just active Defaults. */}
          {isAdmin && showLegalTab && (
            <TabsContent value="default-recovery">
              <DefaultRecoveryTab
                deal={deal}
                getAuthHeaders={getAuthHeaders}
                onRefresh={refetchTransactions}
              />
            </TabsContent>
          )}
        </Tabs>

        {/* Deal Notes */}
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-700">Deal Notes</CardTitle>
              {notesDirty && (
                <Button
                  size="sm"
                  data-testid="save-notes-btn"
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="h-7 text-xs"
                >
                  {savingNotes ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <textarea
              data-testid="deal-notes-textarea"
              className="w-full min-h-[80px] text-sm border rounded-md p-2 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add notes about this deal..."
              value={dealNotes}
              onChange={(e) => { setDealNotes(e.target.value); setNotesDirty(true); }}
              onBlur={() => { if (notesDirty) handleSaveNotes(); }}
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Edit Deal Dialog (Quick Edit) */}
      <EditDealDialog
        open={editDealDialogOpen}
        onOpenChange={setEditDealDialogOpen}
        deal={deal}
        editDealForm={editDealForm}
        onFieldChange={handleEditDealFieldChange}
        onSave={handleSaveDeal}
        saving={savingDeal}
      />

      {/* Part C (Feb 2026) — focused resolution modals. Driven by resolveModalKey
          state populated by handleResolveWarning() dispatch router. */}
      {/* BL-026: CommissionPaidResolutionModal removed — commission is now
          derived from rate × funding_amount; rate edits flow through the
          standard Edit Deal dialog. */}
      <BrokerCommissionResolutionModal
        open={resolveModalKey === 'broker_commission'}
        onOpenChange={(o) => !o && setResolveModalKey(null)}
        deal={deal}
        onResolved={refetch}
      />
      <EstimatedEndDateResolutionModal
        open={resolveModalKey === 'estimated_end_date'}
        onOpenChange={(o) => !o && setResolveModalKey(null)}
        deal={deal}
        onResolved={refetch}
      />
      <MerchantLinkResolutionModal
        open={resolveModalKey === 'merchant_link'}
        onOpenChange={(o) => !o && setResolveModalKey(null)}
        deal={deal}
        onResolved={refetch}
      />
      <BrokerLinkResolutionModal
        open={resolveModalKey === 'broker_link'}
        onOpenChange={(o) => !o && setResolveModalKey(null)}
        deal={deal}
        onResolved={refetch}
      />
      <FunderLinkResolutionModal
        open={resolveModalKey === 'funder_link'}
        onOpenChange={(o) => !o && setResolveModalKey(null)}
        deal={deal}
        onResolved={refetch}
      />

      {/* Contract generation — new / refi (08/20) */}
      <ContractDialog
        open={contractKind !== null}
        onOpenChange={(o) => !o && setContractKind(null)}
        deal={deal}
        kind={contractKind || 'new'}
        getAuthHeaders={getAuthHeaders}
      />

      
      {/* Full Edit Deal Dialog */}
      <FullEditDealDialog
        open={fullEditDialogOpen}
        onOpenChange={setFullEditDialogOpen}
        deal={deal}
        getAuthHeaders={getAuthHeaders}
        onSaveSuccess={() => {
          toast.success('Deal updated successfully');
          refetch();
        }}
        onStatusChangedToDefault={() => {
          // Edit-Deal form just flipped status to Default. The component
          // already routed the request through mark-default (so snapshot
          // + penalty are in place). Open the post-flip workflow.
          refetch();
          setDefaultWorkflowOpen(true);
        }}
      />

      {/* Default-status post-flip workflow dialog (May 2026) */}
      <DefaultStatusWorkflowDialog
        open={defaultWorkflowOpen}
        onOpenChange={setDefaultWorkflowOpen}
        deal={deal}
        onComplete={() => {
          refetch();
        }}
      />
      
      {/* Delete Deal Confirmation Dialog */}
      {/* 10/26 (Josh): floating "+" like the MILA button — shortcut to Add Transaction. Switches to the
          Transactions tab (the dialog is mounted there) and opens it with a fresh form. Admin only, same gate as the tab's button. */}
      {isAdmin && (
        <button
          type="button"
          data-testid="fab-add-transaction"
          title="Add transaction"
          aria-label="Add transaction"
          onClick={() => { setActiveTab('transactions'); setEditingTransaction(null); setTransactionDialogOpen(true); }}
          className="fixed bottom-24 right-6 z-50 h-14 w-14 rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-600/30 flex items-center justify-center hover:bg-emerald-700 hover:scale-105 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
        >
          <Plus className="h-7 w-7" />
        </button>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Deal #{deal?.deal_id}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Are you sure you want to delete this deal? This action cannot be undone.</p>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Deal Name:</span>
                    <span className="font-medium">{deal?.deal_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Shortcode:</span>
                    <span className="font-mono">{deal?.deal_shortcode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Funding Amount:</span>
                    <span className="font-medium">{formatCurrency(deal?.funding_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <span className="font-medium">{deal?.status}</span>
                  </div>
                </div>
                <p className="text-sm text-red-600 font-medium">
                  ⚠️ All transactions, syndications, and documents associated with this deal will also be deleted.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDeal}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete Deal'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Settlement Modal */}
      <SettlementModal
        open={settlementModalOpen}
        onOpenChange={setSettlementModalOpen}
        dealId={id}
        onSettled={() => {
          refetch();
        }}
      />

      {/* Cancel Deal Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent data-testid="cancel-deal-modal">
          <DialogHeader>
            <DialogTitle className="text-gray-700">Cancel Deal #{deal?.deal_id}?</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>This will mark the deal as cancelled. It will remain visible but will be excluded from all financial calculations and reports.</p>
              <p className="text-sm text-gray-500">No transactions, syndications, or documents will be deleted.</p>
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Reason for cancellation (optional)</Label>
            <Input
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              placeholder="e.g. Wire returned, deal fell through"
              data-testid="cancel-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>Keep Deal</Button>
            <Button variant="destructive" onClick={handleCancelDeal} disabled={cancelling} data-testid="confirm-cancel-btn" className="bg-gray-600 hover:bg-gray-700">
              {cancelling ? 'Cancelling...' : 'Cancel Deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* EPO Dialog */}
      <Dialog open={epoDialogOpen} onOpenChange={setEpoDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <DollarSign className="h-5 w-5" />
              Early Pay Off (EPO) — Deal #{deal?.deal_id}
            </DialogTitle>
            <DialogDescription>
              Apply an early payoff discount. This will reduce the payback, adjust commission, update syndication targets, and mark the deal as Paid in Full.
            </DialogDescription>
          </DialogHeader>

          {epoPreview && (
            <div className="space-y-4">
              {/* Current Deal Summary */}
              <div className="bg-gray-50 p-3 rounded-lg space-y-1 text-sm">
                <div className="font-medium text-gray-900 mb-2">Current Deal Info</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <span className="text-gray-600">Original Payback:</span>
                  <span className="font-medium">{formatCurrency(epoPreview.original_payback)}</span>
                  {epoPreview.existing_discounts > 0 && (
                    <>
                      <span className="text-gray-600">Existing Discounts:</span>
                      <span className="font-medium text-orange-600">-{formatCurrency(epoPreview.existing_discounts)}</span>
                    </>
                  )}
                  <span className="text-gray-600">Effective Payback:</span>
                  <span className="font-medium">{formatCurrency(epoPreview.effective_payback)}</span>
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-medium text-green-700">{formatCurrency(epoPreview.amount_paid)}</span>
                  <span className="text-gray-600">Balance Due:</span>
                  <span className="font-bold">{formatCurrency(epoPreview.balance_due)}</span>
                  {epoPreview.total_fees > 0 && (
                    <>
                      <span className="text-gray-600">Outstanding Fees:</span>
                      <span className="font-medium text-red-600">{formatCurrency(epoPreview.total_fees)}</span>
                    </>
                  )}
                  <span className="text-gray-600">Effective Balance:</span>
                  <span className="font-bold">{formatCurrency(epoPreview.effective_balance)}</span>
                  <span className="text-gray-600">Current Commission:</span>
                  <span className="font-medium">{epoPreview.commission_rate || 0}% ({formatCurrency(epoPreview.current_commission_amount)})</span>
                </div>
              </div>

              {/* EPO Fields */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="epoNewPayback">New Payback Amount</Label>
                    <Input
                      id="epoNewPayback"
                      data-testid="epo-new-payback-input"
                      type="number"
                      step="0.01"
                      value={epoForm.newPayback}
                      onChange={(e) => handleEpoPaybackChange(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="epoDiscount">EPO Discount Amount</Label>
                    <Input
                      id="epoDiscount"
                      data-testid="epo-discount-input"
                      type="number"
                      step="0.01"
                      value={epoForm.discountAmount}
                      onChange={(e) => handleEpoDiscountChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Fee Waiver Section - Only show if there are fees */}
                {epoPreview.total_fees > 0 && (
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                    <div className="grid grid-cols-2 gap-3 items-end">
                      <div>
                        <Label htmlFor="epoFeeWaiver" className="text-amber-800">
                          Fee Waiver Amount
                          <span className="block text-xs font-normal text-amber-600">
                            Max: {formatCurrency(epoPreview.total_fees)}
                          </span>
                        </Label>
                        <Input
                          id="epoFeeWaiver"
                          data-testid="epo-fee-waiver-input"
                          type="number"
                          step="0.01"
                          min="0"
                          max={epoPreview.total_fees}
                          value={epoForm.feeWaiverAmount}
                          onChange={(e) => setEpoForm(prev => ({ ...prev, feeWaiverAmount: e.target.value }))}
                          className="border-amber-300 focus:ring-amber-500"
                        />
                      </div>
                      <div className="text-sm text-amber-700 pb-2">
                        <button 
                          type="button"
                          className="text-amber-600 underline hover:text-amber-800"
                          onClick={() => setEpoForm(prev => ({ ...prev, feeWaiverAmount: epoPreview.total_fees.toString() }))}
                        >
                          Waive all fees
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="epoCommRate">New Commission Rate (%)</Label>
                    <Input
                      id="epoCommRate"
                      data-testid="epo-commission-rate-input"
                      type="number"
                      step="0.01"
                      value={epoForm.newCommissionRate}
                      onChange={(e) => setEpoForm(prev => ({ ...prev, newCommissionRate: e.target.value }))}
                    />
                    {epoPreview.funding_amount > 0 && epoForm.newCommissionRate && (
                      <p className="text-xs text-gray-500 mt-1">
                        = {formatCurrency(epoPreview.funding_amount * (parseFloat(epoForm.newCommissionRate) || 0) / 100)}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="epoDate">EPO Date</Label>
                    <Input
                      id="epoDate"
                      data-testid="epo-date-input"
                      type="date"
                      value={epoForm.epoDate}
                      onChange={(e) => setEpoForm(prev => ({ ...prev, epoDate: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="epoNotes">Notes</Label>
                  <textarea
                    id="epoNotes"
                    data-testid="epo-notes-input"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[60px]"
                    value={epoForm.notes}
                    onChange={(e) => setEpoForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Reason for early payoff, negotiated terms, etc."
                  />
                </div>
              </div>

              {/* Impact Preview */}
              {(parseFloat(epoForm.discountAmount) > 0 || parseFloat(epoForm.feeWaiverAmount) > 0) && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg text-sm space-y-1">
                  <div className="font-medium text-emerald-800 mb-1">EPO Impact Summary</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {parseFloat(epoForm.discountAmount) > 0 && (
                      <>
                        <span className="text-emerald-700">EPO Discount:</span>
                        <span className="font-bold text-emerald-800">{formatCurrency(parseFloat(epoForm.discountAmount) || 0)}</span>
                      </>
                    )}
                    {parseFloat(epoForm.feeWaiverAmount) > 0 && (
                      <>
                        <span className="text-amber-700">Fee Waiver:</span>
                        <span className="font-bold text-amber-800">{formatCurrency(parseFloat(epoForm.feeWaiverAmount) || 0)}</span>
                      </>
                    )}
                    <span className="text-emerald-700">New Payback:</span>
                    <span className="font-medium">{formatCurrency(parseFloat(epoForm.newPayback) || 0)}</span>
                    <span className="text-emerald-700">New Factor Rate:</span>
                    <span className="font-medium">
                      {epoPreview.funding_amount > 0 ? ((parseFloat(epoForm.newPayback) || 0) / epoPreview.funding_amount).toFixed(4) : 'N/A'}
                    </span>
                    <span className="text-emerald-700">New Commission:</span>
                    <span className="font-medium">
                      {epoForm.newCommissionRate}% = {formatCurrency(epoPreview.funding_amount * (parseFloat(epoForm.newCommissionRate) || 0) / 100)}
                    </span>
                  </div>
                  {epoPreview.syndications?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-emerald-200">
                      <div className="font-medium text-emerald-700 text-xs mb-1">Syndication Adjustments:</div>
                      {epoPreview.syndications.map(s => {
                        const newTarget = (parseFloat(epoForm.newPayback) || 0) * (s.ownership_percentage / 100);
                        return (
                          <div key={s.id} className="text-xs text-emerald-600">
                            {s.syndicator_name}: {formatCurrency(s.target_collection)} → {formatCurrency(newTarget)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEpoDialogOpen(false)} disabled={processingEpo}>
              Cancel
            </Button>
            <Button
              data-testid="epo-apply-btn"
              onClick={handleApplyEpo}
              disabled={processingEpo || !epoForm.newPayback || (parseFloat(epoForm.discountAmount) <= 0 && parseFloat(epoForm.feeWaiverAmount) <= 0)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {processingEpo ? 'Applying...' : 'Apply EPO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Default Confirmation Dialog */}
      <AlertDialog open={defaultDialogOpen} onOpenChange={setDefaultDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={`flex items-center gap-2 ${defaultPreview?.is_refinanced ? 'text-amber-600' : 'text-red-600'}`}>
              <AlertTriangle className="h-5 w-5" />
              {defaultPreview?.is_refinanced ? 'Deal is Refinanced' : 'Mark Deal as Defaulted'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {/* Refinance Warning */}
                {defaultPreview?.is_refinanced && (
                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                    <div className="font-semibold text-amber-800 mb-2">⚠️ This deal has been refinanced</div>
                    <p className="text-sm text-amber-700">{defaultPreview.refinance_message}</p>
                    {defaultPreview.refi_to_deal_id_number && (
                      <p className="text-sm text-amber-700 mt-1">
                        Refinanced into: <strong>Deal #{defaultPreview.refi_to_deal_id_number}</strong>
                      </p>
                    )}
                    <p className="text-sm text-amber-800 mt-2 font-medium">
                      Are you sure you want to mark this as defaulted instead?
                    </p>
                  </div>
                )}
                
                {/* Default options - show for all cases now */}
                <div>
                  <p className="mb-2">This action will:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Change the deal status to <strong>Defaulted</strong></li>
                    <li>Add a default penalty fee to the deal ledger</li>
                  </ul>
                  
                  <div className="space-y-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Date</label>
                      <input
                        type="date"
                        value={defaultDate}
                        onChange={(e) => setDefaultDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                        data-testid="default-date-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Fee Amount
                        <span className="text-gray-400 font-normal ml-1">(auto: 25% of remaining = {formatCurrency(defaultPreview?.calculation?.fee_amount || 0)})</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={defaultFeeOverride}
                          onChange={(e) => setDefaultFeeOverride(e.target.value)}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          data-testid="default-fee-input"
                        />
                      </div>
                    </div>
                    {/* Send-default-package-email checkbox removed May 2026.
                        Email is now part of the post-flip
                        DefaultStatusWorkflowDialog (step 3). */}
                  </div>
                </div>

                {defaultPreview?.clawback?.should_ask && (
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm cursor-pointer" data-testid="default-clawback-ask">
                    <input type="checkbox" className="mt-1" checked={defaultClawback} onChange={(e) => setDefaultClawback(e.target.checked)} />
                    <span>
                      <b>Is this a clawback?</b> This deal is only <b>{defaultPreview.clawback.business_days_since_funding} business day{defaultPreview.clawback.business_days_since_funding === 1 ? '' : 's'}</b> past funding
                      (window {defaultPreview.clawback.window_business_days}). Commission paid: <b>{formatCurrency(defaultPreview.clawback.commission_amount)}</b>
                      {defaultPreview.clawback.broker_name ? ` to ${defaultPreview.clawback.broker_name}` : ''}.
                      <span className="block text-xs text-amber-800 mt-1">Checked = request it back now (tracked until you mark it recovered). Unchecked = you can still claw it back later from the Default tab.</span>
                    </span>
                  </label>
                )}
                
                {defaultPreview && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                    <div className="font-medium text-gray-900">
                      {defaultPreview?.is_refinanced ? 'Fee Calculation (if defaulted anyway):' : 'Default Fee Calculation:'}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <span className="text-gray-600">Remaining Payback:</span>
                      <span className="font-medium">{formatCurrency(defaultPreview.calculation?.remaining_payback)}</span>
                      <span className="text-gray-600">Default Fee Rate:</span>
                      <span className="font-medium">{defaultPreview.calculation?.fee_rate}</span>
                      <span className="text-gray-600 font-semibold">Default Fee Amount:</span>
                      <span className="font-bold text-red-600">{formatCurrency(defaultPreview.calculation?.fee_amount)}</span>
                    </div>
                    
                    {defaultPreview.breakdown && (
                      <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                        <details>
                          <summary className="cursor-pointer hover:text-gray-700">View calculation breakdown</summary>
                          <div className="mt-2 space-y-1">
                            <div>Original Payback: {formatCurrency(defaultPreview.breakdown.original_payback)}</div>
                            <div>Discounts: -{formatCurrency(defaultPreview.breakdown.total_discounts)}</div>
                            <div>Effective Payback: {formatCurrency(defaultPreview.breakdown.effective_payback)}</div>
                            <div>Paid to Date: {formatCurrency(defaultPreview.breakdown.paid_to_date_successful)}</div>
                            <div>Refunds: -{formatCurrency(defaultPreview.breakdown.total_refunds)}</div>
                            <div>Net Collections: {formatCurrency(defaultPreview.breakdown.net_collections)}</div>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                )}
                
                {!defaultPreview?.is_refinanced && (
                  <p className="text-amber-600 text-sm font-medium">
                    This action can be reversed later if needed.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={processingDefault}>Cancel</AlertDialogCancel>
            {defaultPreview?.is_refinanced && (
              <Button
                onClick={handleMarkAsRefinanced}
                disabled={processingDefault}
                variant="outline"
                className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
              >
                Mark as Refinanced Instead
              </Button>
            )}
            <AlertDialogAction
              onClick={() => handleMarkAsDefault(defaultPreview?.is_refinanced)}
              disabled={processingDefault}
              className="bg-red-600 hover:bg-red-700"
            >
              {processingDefault ? 'Processing...' : (defaultPreview?.is_refinanced ? 'Default Anyway' : 'Mark as Defaulted')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reverse Default / leave-Default confirmation (11/02): lists what the pre-default reset removes */}
      <DefaultResetDialog
        open={reverseDefaultDialogOpen}
        onOpenChange={setReverseDefaultDialogOpen}
        preview={defaultResetPreview}
        loading={defaultResetLoading}
        targetStatus={defaultResetTarget}
        processing={processingDefault}
        onConfirm={confirmDefaultReset}
      />

      {/* Refinance Wizard */}
      <RefinanceWizard
        open={refinanceWizardOpen}
        onOpenChange={setRefinanceWizardOpen}
        getAuthHeaders={getAuthHeaders}
        currentDealId={deal?.id}
        currentDealNumber={deal?.deal_id}
        mode={refinanceWizardMode}
        onSuccess={refetch}
      />

      {/* Transfer Credit Dialog */}
      <Dialog open={transferCreditDialogOpen} onOpenChange={setTransferCreditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <ArrowRightLeft className="h-5 w-5" />
              Transfer Credit to Another Deal
            </DialogTitle>
            <DialogDescription>
              Transfer the overage/credit from Deal #{deal?.deal_id} to another deal. This will create matching adjustment transactions on both deals.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Source Deal Info */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-700 mb-2 font-medium">Source Deal</div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Deal #{deal?.deal_id} - {deal?.deal_name}</span>
                <span className="font-semibold text-blue-600">{formatCurrency(Math.abs(effectiveBalance))} credit</span>
              </div>
            </div>
            
            {/* Target Deal Selection — typeahead search */}
            <div className="space-y-2">
              <Label htmlFor="targetDeal">Target Deal</Label>
              {(() => {
                const selectedDeal = allDeals.find(d => d.id === transferCreditForm.targetDealId);
                const q = transferTargetSearch.trim().toLowerCase();
                const filtered = allDeals
                  .filter(d => d.id !== deal?.id)
                  .filter(d => {
                    if (!q) return true;
                    const idStr = String(d.deal_id || '').toLowerCase();
                    const nameStr = String(d.deal_name || d.deal_shortcode || '').toLowerCase();
                    return idStr.includes(q) || nameStr.includes(q);
                  })
                  .slice(0, 50);

                return (
                  <div className="relative">
                    {selectedDeal && !transferTargetOpen ? (
                      <div className="flex items-center justify-between border border-gray-300 rounded-md px-3 py-2 bg-blue-50" data-testid="target-deal-selected">
                        <div className="text-sm">
                          <span className="font-semibold">#{selectedDeal.deal_id}</span>
                          <span className="text-gray-700 ml-2">{selectedDeal.deal_name || selectedDeal.deal_shortcode || 'Unnamed'}</span>
                          <span className="text-gray-500 ml-2 text-xs">({formatCurrency(selectedDeal.balance || 0)} balance)</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            setTransferCreditForm(prev => ({ ...prev, targetDealId: '' }));
                            setTransferTargetSearch('');
                            setTransferTargetOpen(true);
                          }}
                          data-testid="target-deal-change"
                        >
                          Change
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Input
                          id="targetDeal"
                          value={transferTargetSearch}
                          autoFocus={transferTargetOpen}
                          onChange={(e) => { setTransferTargetSearch(e.target.value); setTransferTargetOpen(true); }}
                          onFocus={() => setTransferTargetOpen(true)}
                          onBlur={() => setTimeout(() => setTransferTargetOpen(false), 150)}
                          placeholder="Type deal ID (e.g. 10757) or deal name…"
                          data-testid="target-deal-search-input"
                        />
                        {transferTargetOpen && (
                          <div className="absolute z-50 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-300 rounded-md shadow-lg" data-testid="target-deal-results">
                            {filtered.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-gray-500">No deals match "{transferTargetSearch}"</div>
                            ) : (
                              filtered.map(d => (
                                <button
                                  type="button"
                                  key={d.id}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setTransferCreditForm(prev => ({ ...prev, targetDealId: d.id }));
                                    setTransferTargetSearch('');
                                    setTransferTargetOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                  data-testid={`target-deal-option-${d.deal_id}`}
                                >
                                  <span className="font-semibold">#{d.deal_id}</span>
                                  <span className="text-gray-700 ml-2">{d.deal_name || d.deal_shortcode || 'Unnamed'}</span>
                                  <span className="text-gray-500 ml-2 text-xs">({formatCurrency(d.balance || 0)})</span>
                                </button>
                              ))
                            )}
                            {filtered.length === 50 && (
                              <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50">Showing first 50 — keep typing to narrow</div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            
            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="transferAmount">Amount to Transfer</Label>
              <Input
                id="transferAmount"
                type="number"
                step="0.01"
                min="0.01"
                max={Math.abs(effectiveBalance)}
                value={transferCreditForm.amount}
                onChange={(e) => setTransferCreditForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount..."
              />
              <p className="text-xs text-gray-500">
                Maximum available: {formatCurrency(Math.abs(effectiveBalance))}
              </p>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="transferNotes">Notes (optional)</Label>
              <textarea
                id="transferNotes"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={2}
                value={transferCreditForm.notes}
                onChange={(e) => setTransferCreditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes for this transfer..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setTransferCreditDialogOpen(false);
                setTransferCreditForm({ targetDealId: '', amount: '', notes: '' });
                setTransferTargetSearch('');
                setTransferTargetOpen(false);
              }}
              disabled={transferringCredit}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleTransferCredit}
              disabled={transferringCredit || !transferCreditForm.targetDealId || !transferCreditForm.amount}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {transferringCredit ? 'Transferring...' : 'Transfer Credit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Document Dialog */}
      <ComposeDealEmailDialog dealId={id} dealNumber={deal?.deal_id} open={composeOpen} onOpenChange={setComposeOpen} getAuthHeaders={getAuthHeaders} />
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Email Document
            </DialogTitle>
            <DialogDescription>
              Send the generated PDF as an email attachment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Recipient Email *</Label>
              {emailRecipients.length > 0 ? (
                <div className="space-y-2">
                  <select
                    className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm"
                    value={emailRecipient}
                    onChange={e => setEmailRecipient(e.target.value)}
                    data-testid="email-recipient-select"
                  >
                    {emailRecipients.map(r => (
                      <option key={r.email} value={r.email}>{r.label} ({r.email})</option>
                    ))}
                    <option value="__custom">Other (type below)</option>
                  </select>
                  {emailRecipient === '__custom' && (
                    <Input
                      type="email"
                      placeholder="recipient@example.com"
                      onChange={e => setEmailRecipient(e.target.value)}
                      data-testid="email-recipient-custom"
                    />
                  )}
                </div>
              ) : (
                <Input
                  type="email"
                  value={emailRecipient}
                  onChange={e => setEmailRecipient(e.target.value)}
                  placeholder="recipient@example.com"
                  data-testid="email-recipient-input"
                />
              )}
            </div>
            <SenderSelect sender={emailSender} value={emailSenderId} onChange={setEmailSenderId} />
            <div>
              <Label>Subject (optional)</Label>
              <Input
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                placeholder="Auto-generated if blank"
              />
            </div>
            <div>
              <Label>Message (optional)</Label>
              <textarea
                className="w-full px-3 py-2 border rounded-md text-sm min-h-[80px]"
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
                placeholder="Auto-generated if blank"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !emailRecipient || emailRecipient === '__custom'} data-testid="send-email-btn">
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default DealDetail;
